import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Device } from "@/lib/signalwireSoftphone";
import * as ringback from "@/lib/ringbackTone";
import DialerBatchHeader from "@/components/DialerBatchHeader";
import DialerLeadInfoPanel from "@/components/DialerLeadInfoPanel";
import QuickOfferButtons from "@/components/QuickOfferButtons";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Phone, PhoneOff, PhoneCall, Mic, MicOff, Hash, ChevronRight,
  Loader2, MapPin, User, PhoneMissed, Voicemail, Ban, Clock,
  Flame, ThumbsDown, ThumbsUp, PhoneForwarded, Sparkles,
  BookText, Settings, Save, Trash2, Plus, VolumeX,
  Home as HomeIcon, DollarSign, FileSignature, TrendingUp, Wrench,
  Zap, Shield, ShieldCheck, ShieldAlert, X, Pencil, Play, PauseCircle, PlayCircle,
  Coffee, UtensilsCrossed, Users2, GraduationCap, LogOut, ArrowRightCircle,
} from "lucide-react";

const DISPOSITIONS = [
  { key: "connected",       label: "Talked", icon: PhoneCall, tone: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { key: "interested-hot",  label: "Hot lead", icon: Flame,   tone: "bg-orange-100 text-orange-800 border-orange-300" },
  { key: "interested-warm", label: "Warm",     icon: ThumbsUp, tone: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "market-value",    label: "Market Value", icon: HomeIcon, tone: "bg-violet-100 text-violet-800 border-violet-300" },
  { key: "callback",        label: "Callback", icon: Clock,   tone: "bg-blue-100 text-blue-700 border-blue-200" },
  { key: "voicemail",       label: "Voicemail", icon: Voicemail, tone: "bg-purple-100 text-purple-700 border-purple-200" },
  { key: "no-answer",       label: "No answer", icon: PhoneMissed, tone: "bg-slate-100 text-slate-700 border-slate-200" },
  { key: "not-interested",  label: "Not interested", icon: ThumbsDown, tone: "bg-gray-100 text-gray-700 border-gray-200" },
  { key: "wrong-number",    label: "Wrong #",  icon: PhoneOff, tone: "bg-rose-100 text-rose-700 border-rose-200" },
  { key: "dnc",             label: "DNC",      icon: Ban,     tone: "bg-red-200 text-red-800 border-red-300" },
];

const e164 = (raw) => {
  const d = (raw || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  if (raw && raw.trim().startsWith("+")) return raw.trim();
  return "";
};

export default function Dialer() {
  // ---- DIAGNOSTIC (temporary) — logged once per mount so we can confirm
  // which host the preview build is talking to. Remove after the silent-
  // dial bug is root-caused.
  useEffect(() => {
    console.log("[FRONTEND] Dialer mounted", {
      REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
      axios_baseURL: api?.defaults?.baseURL,
      window_href: (typeof window !== "undefined") ? window.location.href : "n/a",
      window_origin: (typeof window !== "undefined") ? window.location.origin : "n/a",
      NODE_ENV: process.env.NODE_ENV,
    });
  }, []);
  const { user, role } = useAuth();
  const isAdmin = role === "admin" || user?.role === "admin";
  const [status, setStatus] = useState({ configured: false, caller_id_pool: [] });
  const [deviceState, setDeviceState] = useState("idle");
  const [callState, setCallState]     = useState("idle"); // idle|connecting|ringing|connected|ended
  // Separate media-connection status so we can show a clear pill:
  //   "joining"   → browser is negotiating SIP + WebRTC (customer may
  //                  be ringing already but rep can't hear yet)
  //   "connected" → browser leg is fully in the conference, 2-way audio
  //   "failed"    → SIP/WSS/ICE couldn't establish (customer answered
  //                  into silence → TCPA abandonment risk)
  //   "ended"     → normal hangup after a successful connect
  //   null        → no active audio session
  const [mediaStatus, setMediaStatus] = useState(null);
  // Auto-clear the media-status pill when the call fully returns to
  // idle so a red "Audio failed" from the previous call doesn't linger
  // into the next dial.
  useEffect(() => {
    if (callState === "idle") setMediaStatus(null);
  }, [callState]);
  // ---- Ringback tone (owner ask 2026-08-28) ----
  // Ringback tone DISABLED 2026-08-28 per owner ask: reps were hearing
  // synthetic 440/480Hz rings BEFORE the real SIP ring reached the
  // seller — sounded like "wasted" rings. We now let the real
  // WebRTC audio track drive the ringing sound (SignalWire streams
  // the actual PSTN ringback back to the browser), so what the rep
  // hears == what the seller's phone is doing. Zero fake tones.
  //
  // The `dialer_prefs.ringback_enabled` admin toggle still exists —
  // set it to `true` from Settings if you want the synthetic tone
  // back. Default is now OFF (see DIALER_PREFS_DEFAULT server-side).
  useEffect(() => {
    ringback.stop();
  }, [callState]);
  // Unmount safety — never leave a tone playing after leaving the page.
  useEffect(() => () => ringback.stop(), []);
  const [queue, setQueue]             = useState([]);
  const [queueBusy, setQueueBusy]     = useState(false);
  const [current, setCurrent]         = useState(null);   // lead being called
  const [callSid, setCallSid]         = useState(null);
  const [callStartedAt, setCallStartedAt] = useState(null);
  const [muted, setMuted]             = useState(false);
  const [notes, setNotes]             = useState("");
  const [showDispo, setShowDispo]     = useState(false);
  // Wave 2: wrap-up countdown. When the dispo dialog opens we start a
  // 30s countdown. If the rep doesn't click ANY disposition before it
  // hits zero, we auto-save "no-answer" and advance so they can't camp
  // in wrap-up forever between calls (matches Enzo's enforced wrap).
  const [wrapCountdown, setWrapCountdown] = useState(0);
  const [autoAdvance, setAutoAdvance] = useState(true);
  // Batch-Dialer look tabs (owner ask 2026-08-28). Purely a UI tab
  // state — data rendered underneath still lives in the same cards.
  const [headerTab, setHeaderTab] = useState("lead_info");
  // Tenant-wide dialer prefs (admin publishes from Settings → Dialer).
  // Ref so hot paths (dial, hangup, auto-dispose) never re-render.
  const dialerPrefsRef = useRef({
    inter_call_gap_ms: 500, auto_dispose_delay_sec: 6,
    auto_dispose_key: "no-answer", max_ring_sec: 25,
    ringback_enabled: true, amd_enabled: true,
    wrap_timeout_sec: 30, warm_transfer_number: "",
    preload_next: true, persistent_session: true,
  });
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const { data } = await api.get("/tenant/dialer-prefs");
        if (!stop && data?.dialer_prefs) {
          dialerPrefsRef.current = { ...dialerPrefsRef.current, ...data.dialer_prefs };
        }
      } catch { /* silent — defaults keep working */ }
      finally { if (!stop) setPrefsLoaded(true); }
    })();
    return () => { stop = true; };
  }, []);
  const [dispoChoice, setDispoChoice] = useState(null);   // for callback picker
  const [callbackAt, setCallbackAt]   = useState("");
  // ---- Scripts & Voicemail Drops ----
  const [scripts, setScripts]         = useState([]);
  const [activeScript, setActiveScript] = useState(null);
  const [voicemails, setVoicemails]   = useState([]);
  const [droppingVm, setDroppingVm]   = useState(false);
  const [manageOpen, setManageOpen]   = useState(null); // "scripts"|"vms"|null
  // ---- Manual dial pad ----
  const [manualNumber, setManualNumber] = useState("");
  // ---- Board / column filter (which CRM column to pull leads from) ----
  const [boards, setBoards] = useState([]);
  const [filterBoardId, setFilterBoardId] = useState("");   // "" = all boards
  const [filterColumnKey, setFilterColumnKey] = useState(""); // "" = all cols
  // ---- Live Property panel ----
  const [propInfo, setPropInfo] = useState(null);    // {arv, suggested_offer, ...}
  const [tiers, setTiers] = useState(null);          // 3-tier smart rec
  const [activeCallerId, setActiveCallerId] = useState("");  // number we dialed FROMommendation
  const [selectedTier, setSelectedTier] = useState("quick");   // Quick is now the default tier (60%)
  const [offerSentThisCall, setOfferSentThisCall] = useState(false);
  const [tierNudgeOptIn, setTierNudgeOptIn] = useState(true);  // default on — small delight
  const [propLoading, setPropLoading] = useState(false);
  const [offerPrice, setOfferPrice] = useState("");  // editable in the panel
  const [sellerEmail, setSellerEmail] = useState("");
  const [knownEmails, setKnownEmails] = useState([]);   // dropdown options
  const [emailMode, setEmailMode]     = useState("select"); // "select" | "new"
  const [ownerPrice, setOwnerPrice]   = useState("");   // seller's asking price
  const [conditionKey, setConditionKey] = useState("cosmetic"); // repair chip — default Cosmetic per user policy
  const [tiersLoading, setTiersLoading] = useState(false);
  const [sendingOffer, setSendingOffer] = useState(false);
  // ---- Predictive Dialer (Enzo-parity, always 3-line + auto-continue) ----
  // No user-facing controls: rep clicks Start Dialing and the system
  // takes over until Pause. Break codes captured on pause.
  // 2026-08-21: Retired the 3-line predictive dialer per rep feedback.
  // Auto-Dial is now strictly single-line: one call at a time, and the
  // moment a dispo is saved the queue auto-advances to the next lead.
  // These consts are kept as `1` so the existing lock-bulk + prepare
  // guards still behave sanely (they slice by `powerLines`).
  const powerLines = 1;
  const predictiveOn = false;
  const powerDoubleDial = false;
  const [powerSession, setPowerSession] = useState(null); // { session_id, conference_name, legs }
  const [powerBusy, setPowerBusy] = useState(false);
  const [powerVmDropOn, setPowerVmDropOn] = useState(false); // OFF by default — admin paused all VM drops
  // ---- Break-code modal (Wave 2) ----
  // Rep can't just close the dialer session; they Pause and must pick a
  // reason (Bathroom / Lunch / Coaching / Meeting / Training / Done for day).
  // The pause + reason + duration feeds shift-report analytics.
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [breakBusy, setBreakBusy] = useState(false);
  const [activeBreak, setActiveBreak] = useState(null); // { id, reason, started_at }
  const [compliance, setCompliance] = useState(null); // { abandoned, connected, abandon_rate, compliant }
  // ---- Acquisitions warm-transfer (owner ask 2026-08-25) ----
  // Admin sets the acquisitions manager number in Settings → Dialer.
  // On active call, rep clicks Transfer → we dial that number into the
  // same conference. Rep + seller + manager become a 3-way, rep hangs
  // up and disposes, seller ↔ manager keep talking.
  const [transferNumber, setTransferNumber] = useState("");
  const [transferCanEdit, setTransferCanEdit] = useState(false);
  const [transferEditing, setTransferEditing] = useState(false);
  const [transferInput, setTransferInput] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [transferred, setTransferred] = useState(false);
  // Speed-dial telemetry (owner ask 2026-08-26): capture the exact
  // moment the rep clicks a disposition so we can measure the gap
  // until the next dial fires. Target ≤2s.
  const dispoSubmittedAtRef = useRef(null);
  // ---- In-call Offer popup (auto-opens when a live customer picks up) ----
  // User setting persisted so a rep can turn it off if they find it in the
  // way. Defaults ON — the whole point is context the instant the seller
  // says "hello".
  const [autoOfferPopup, setAutoOfferPopup] = useState(() => {
    try { return localStorage.getItem("dialer.autoOfferPopup") !== "0"; }
    catch { return true; }
  });
  const [showOfferPopup, setShowOfferPopup] = useState(false);
  // Track the previous callState so we only fire the popup on the
  // idle/ringing/connecting → connected transition, not on every render.
  const prevCallStateRef = useRef("idle");
  const powerPollRef = useRef(null);
  // Explicit call mode. Every entry point that isn't Speed/Power Dial
  // (Manual Dial, per-lead Call button, AI Lead Manager Call Seller,
  // CRM → Call, SMS → Call) treats this as "single" — hangup ends the
  // session cleanly with no auto-advance to the next queue lead.
  // `startPowerDial` flips it to "power" for its multi-line flow.
  // 2026-08-19: user explicitly requested this separation to stop the
  // dialer from silently continuing to dial after a manual/one-off call.
  const callModeRef = useRef("single");
  // Holds the polling-starter closure so call.on("accept") (which fires
  // AFTER /originate returns the power_dial_session_id) can kick off
  // the /power-dial/status polling loop.
  const startPowerPollingRef = useRef(null);
  // Per-tab session token used by the soft call-lock so 3 employees never
  // dial the same lead at the same time. Regenerated on each page load —
  // if a rep opens two tabs, each tab treats itself as a separate dialer.
  const sessionTokenRef = useRef(null);
  if (!sessionTokenRef.current) {
    try {
      const stored = sessionStorage.getItem("dialer.sessionToken");
      if (stored && stored.length >= 8) sessionTokenRef.current = stored;
    } catch { /* ignore */ }
  }
  if (!sessionTokenRef.current) {
    sessionTokenRef.current = `sess-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    try { sessionStorage.setItem("dialer.sessionToken", sessionTokenRef.current); } catch { /* ignore */ }
  }
  // Lead ID currently held under a lock BY THIS TAB — so we know what to
  // release when the call ends. Ref (not state) — we don't need a re-render.
  const activeLockRef = useRef(null);
  // Current call intent id (from POST /api/dialer/prepare) — set once
  // the browser INVITE goes out and cleared after the call ends. Used
  // by call.on("accept") to POST /api/dialer/originate/{intent_id}
  // AFTER the browser leg is fully joined (never before).
  const currentIntentIdRef = useRef(null);
  // Admin-only bulk delete
  const [selectMode, setSelectMode]     = useState(false);
  const [selectedIds, setSelectedIds]   = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const toggleSelected = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllInQueue = () => setSelectedIds(new Set(queue.map((l) => l.id)));
  // Auto-scroll the currently-active lead into view within the compact queue
  useEffect(() => {
    if (!current?.id) return;
    const idx = queue.findIndex((l) => l.id === current.id);
    if (idx < 0) return;
    const el = document.querySelector(`[data-testid="dialer-queue-item-${idx}"]`);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [current?.id, queue]);

  // Auto-offer popup DISABLED 2026-08-28 per owner ask: reps found the
  // full-screen in-call cockpit disruptive during ringing. The 3-tier
  // offer ladder already lives under the phones column in the Lead Info
  // tab, so all context is one glance away without a modal on top.
  //
  // The `setShowOfferPopup(true)` call is still wired to:
  //   • the manual "Preview" chips in QuickOfferButtons (row 1)
  //   • the "Send Contract" chips in QuickOfferButtons (row 2 — fires
  //     `sendOfferNow` directly, popup opens with the tier pre-selected)
  // Reps get the popup on-demand, never automatically.
  const autoOpenedForCallRef = useRef(null);
  useEffect(() => {
    // Ended / idle: close popup and reset our "did we auto-open" flag.
    if (callState === "ended" || callState === "idle") {
      setShowOfferPopup(false);
      autoOpenedForCallRef.current = null;
    }
  }, [callState]);

  // ============================================================ past offers
  // Load offers previously sent to this lead so the modal shows a running
  // history mid-call (rep can reference last-quoted number).
  const [pastOffers, setPastOffers] = useState([]);
  useEffect(() => {
    if (!current?.id || current.id.startsWith("manual-")) {
      setPastOffers([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/offers`);
        const all = Array.isArray(data) ? data : (data?.offers || []);
        // Filter to just this lead's offers — the endpoint returns tenant-wide
        const mine = all.filter((o) => o.lead_id === current.id);
        if (!cancelled) setPastOffers(mine);
      } catch { if (!cancelled) setPastOffers([]); }
    })();
    return () => { cancelled = true; };
  }, [current?.id]);

  // Auto-run Property Lookup when the modal opens on a lead that has an
  // address but no ARV yet. Non-blocking — the tiers will fill themselves
  // in 5–15 seconds while the rep works the seller.
  const [autoLookupBusy, setAutoLookupBusy] = useState(false);
  const runPropertyLookupNow = useCallback(async () => {
    if (!current?.id || current.id.startsWith("manual-")) return;
    const addr = propInfo?.address || current?.property_address;
    if (!addr) {
      toast.error("No property address on this lead to look up");
      return;
    }
    setAutoLookupBusy(true);
    try {
      await api.post("/properties/enrich", { address: addr });
      // Re-fetch the property info so the modal populates.
      const { data } = await api.get(`/dialer/lead-property/${current.id}`);
      setPropInfo(data);
      toast.success("Property data loaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Property lookup failed");
    } finally {
      setAutoLookupBusy(false);
    }
  }, [current?.id, current?.property_address, propInfo?.address]);
  useEffect(() => {
    if (!showOfferPopup) return;
    const arvNum = Number(propInfo?.arv) || 0;
    if (arvNum > 0) return;                    // already have data
    if (autoLookupBusy) return;                // already running
    // Fire once when the modal opens without ARV.
    runPropertyLookupNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOfferPopup]);

  const bulkDelete = async () => {    if (!selectedIds.size) return;
    if (!window.confirm(`Delete ${selectedIds.size} lead${selectedIds.size === 1 ? "" : "s"} permanently? This cannot be undone.`)) return;
    setBulkDeleting(true);
    let ok = 0, fail = 0;
    for (const id of Array.from(selectedIds)) {
      try {
        await api.delete(`/leads/${id}`);
        ok += 1;
      } catch (_) {
        fail += 1;
      }
    }
    setBulkDeleting(false);
    setQueue((q) => q.filter((l) => !selectedIds.has(l.id)));
    if (current && selectedIds.has(current.id)) {
      setCurrent(null);
      setCallState("idle");
    }
    clearSelection();
    setSelectMode(false);
    if (fail === 0) toast.success(`Deleted ${ok} lead${ok === 1 ? "" : "s"}`);
    else toast.error(`Deleted ${ok}, failed ${fail}`);
  };
  useEffect(() => {
    try { localStorage.setItem("dialer.powerLines", String(powerLines)); } catch (_) { /* ignore */ }  }, [powerLines]);

  // Wave 2 — fetch active break on mount. If rep left the tab open with an
  // open break, don't let them start dialing until they End Break.
  // Also polls every 15s so blended-inbound auto-pauses appear promptly.
  useEffect(() => {
    let cancelled = false;
    const poll = () => {
      api.get("/dialer/break/current")
        .then(({ data }) => {
          if (cancelled) return;
          if (data?.active) setActiveBreak(data);
          else setActiveBreak(null);
        })
        .catch(() => { /* ignore */ });
    };
    poll();
    const iv = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Enzo-parity Today stats + live clock. Refresh every 30s.
  const [repStats, setRepStats] = useState({
    total: 0, outbound: 0, inbound: 0, sales: 0,
    close_rate_pct: 0, avg_talk_time: "0:00", active_callbacks: 0,
  });
  const [wallClock, setWallClock] = useState(new Date());
  useEffect(() => {
    const load = () => {
      api.get("/dialer/rep-stats-today").then(({ data }) => setRepStats(data)).catch(() => {});
    };
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const iv = setInterval(() => setWallClock(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  // Release any held call-lock when the rep navigates away. Uses axios so
  // the auth header is included; if the browser cancels the request during
  // unload the 3-min TTL will free the lock automatically as a backstop.
  useEffect(() => {
    const release = () => {
      const lid = activeLockRef.current;
      if (!lid) return;
      activeLockRef.current = null;
      api.post(`/dialer/unlock/${lid}`, { session_token: sessionTokenRef.current }).catch(() => {});
    };
    window.addEventListener("beforeunload", release);
    return () => {
      window.removeEventListener("beforeunload", release);
      release();
    };
  }, []);

  const deviceRef = useRef(null);
  const callRef   = useRef(null);
  const tokenTimerRef = useRef(null);

  // Read ?leadId=... — used to auto-dial when arriving from the lead drawer.
  // Also supports ?phone=… (+ optional ?name=…&?address=…) for synthetic
  // dials from surfaces that don't have a CRM lead yet — e.g. PropertyLookup
  // skip-trace phone rows or the Pipeline "Call" button on a card that
  // hasn't been added to CRM.
  const [searchParams, setSearchParams] = useSearchParams();
  const autoDialLeadId  = searchParams.get("leadId");
  const autoDialPhone   = searchParams.get("phone");
  const autoDialName    = searchParams.get("name");
  const autoDialAddress = searchParams.get("address");
  const autoDialTriedRef = useRef(false);

  // ============================================================ status probe
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dialer/status");
        setStatus(data);
      } catch (e) {
        toast.error("Dialer status check failed");
      }
    })();
  }, []);

  // Load the tenant's acquisitions transfer number (used by the
  // Transfer button + shown as inline editor for admins).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/transfer-number");
        setTransferNumber(data.transfer_number || "");
        setTransferCanEdit(!!data.can_edit);
        setTransferInput(data.transfer_number || "");
      } catch (_) { /* non-fatal */ }
    })();
  }, []);

  // Reset the transferred flag whenever a new call starts so the button
  // re-arms for the next lead.
  useEffect(() => {
    if (callState === "idle" || callState === "connecting") {
      setTransferred(false);
    }
  }, [callState]);

  const saveTransferNumber = async () => {
    try {
      const { data } = await api.put("/settings/transfer-number", {
        transfer_number: transferInput || "",
      });
      setTransferNumber(data.transfer_number || "");
      setTransferEditing(false);
      toast.success(data.transfer_number
        ? `Transfer number set to ${data.transfer_number}`
        : "Transfer number cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const transferToAcquisitions = async () => {
    if (!current?.id) {
      toast.error("No active lead to transfer");
      return;
    }
    if (!transferNumber) {
      toast.error("Admin must set an Acquisitions number in Settings first");
      return;
    }
    setTransferring(true);
    try {
      const { data } = await api.post(`/dialer/transfer/${current.id}`);
      setTransferred(true);
      toast.success(
        data.already_transferred
          ? "Already transferred — hang up when the manager takes over"
          : `Ringing acquisitions at ${data.acquisitions_number} — hang up once they answer`,
        { duration: 8000 }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    } finally {
      setTransferring(false);
    }
  };

  // Read `list` from URL query so "Dial this list" from Call Lists page works
  const [listNameFilter, setListNameFilter] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("list") || ""; }
    catch { return ""; }
  });

  // ============================================================ queue loader
  // Wave 3 quick-filter: min motivation. 0 = show all.
  const [minMotivation, setMinMotivation] = useState(0);

  const loadQueue = useCallback(async () => {
    setQueueBusy(true);
    try {
      // Load the entire filtered list — no artificial page cap so reps
      // can see every lead in the selected list, board column, or filter
      // without paging. Backend caps at 10 000 which is well beyond any
      // realistic dial-session size.
      const params = { limit: 10000, session_token: sessionTokenRef.current };
      if (filterBoardId)  params.board_id  = filterBoardId;
      if (filterColumnKey) params.column_key = filterColumnKey;
      if (listNameFilter)  params.list_name  = listNameFilter;
      if (minMotivation > 0) params.min_motivation = minMotivation;
      const { data } = await api.get("/dialer/queue", { params });
      setQueue(data.leads || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load queue");
    } finally {
      setQueueBusy(false);
    }
  }, [filterBoardId, filterColumnKey, listNameFilter, minMotivation]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  // Auto-refresh queue every 15s so live locks from OTHER reps disappear
  // from this rep's view without a manual reload. Cheap query, tenant-scoped.
  // Pauses while THIS rep has a live call/power session (queue is frozen
  // during a call anyway).
  useEffect(() => {
    const iv = setInterval(() => {
      if (callState !== "idle") return;
      if (powerSession) return;
      if (queueBusy) return;
      loadQueue();
    }, 15000);
    return () => clearInterval(iv);
  }, [loadQueue, callState, powerSession, queueBusy]);

  // ============================================================ boards
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/boards");
        setBoards(data.boards || []);
      } catch (_) { /* ignore */ }
    })();
  }, []);

  // ============================================================ call lists
  // Named lists (from CSV imports on /call-lists). Reps often want to dial
  // "just this list" so they can work a specific campaign end-to-end
  // without leads from other lists leaking in.
  const [callLists, setCallLists] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leads/lists");
        // Backend returns { lists: [...] } with total / with_phone / etc.
        // Sort by most-recently-added so the list the user just imported
        // shows up first.
        const rows = (data?.lists || data || []).filter((l) => l.name);
        setCallLists(rows);
      } catch (_) { /* ignore */ }
    })();
  }, []);

  // ============================================================ live property
  // Whenever a lead becomes the "current" (dialed) lead, fetch its property
  // info + suggested offer so the panel below the dialer stays in sync.
  useEffect(() => {
    if (!current || current.id?.startsWith("manual-")) {
      setPropInfo(null); setOfferPrice(""); setSellerEmail("");
      setKnownEmails([]); setEmailMode("select");
      setOwnerPrice(""); setConditionKey("medium"); setTiers(null);
      return;
    }
    setOfferSentThisCall(false);  // reset — new call, new opportunity for nudge
    setPropLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/dialer/lead-property/${current.id}`);
        setPropInfo(data);
        setOfferPrice(data.suggested_offer ? String(Math.round(data.suggested_offer)) : "");
        // Merge lead email + skip-trace cache emails, dedupe, primary first.
        const merged = [];
        const seen = new Set();
        const pushEmail = (raw) => {
          const v = (raw || "").trim();
          if (!v || !v.includes("@")) return;
          const k = v.toLowerCase();
          if (seen.has(k)) return;
          seen.add(k); merged.push(v);
        };
        pushEmail(current.email);
        (data.known_emails || []).forEach(pushEmail);
        setKnownEmails(merged);
        setSellerEmail(merged[0] || "");
        setEmailMode(merged.length > 0 ? "select" : "new");

        // Reset per-call inputs. Default chip = COSMETIC for every lead
        // (user policy 2026-02-15) — reps override upward on damage cues.
        setOwnerPrice("");
        setConditionKey("cosmetic");
        setTiers(null);
      } catch (_) {
        setPropInfo(null);
        setTiers(null);
      } finally {
        setPropLoading(false);
      }
    })();
  }, [current]);

  // ============================================================ smart offer
  // Re-runs whenever the property loads OR the rep tweaks Owner Price / repair
  // chip. Keeps the tier chips live during the call.
  useEffect(() => {
    if (!propInfo || !propInfo.arv || propInfo.arv <= 0) {
      // Loudly flag this — every "chips don't change offer" report has come
      // down to a lead with no ARV pulled, which silently skips the fetch.
      // eslint-disable-next-line no-console
      console.log("[smart-offer] SKIPPED — no propInfo.arv", { propInfo });
      return;
    }
    // eslint-disable-next-line no-console
    console.log("[smart-offer] fetch start", { conditionKey, ownerPrice, arv: propInfo?.arv });
    const opNum = Number(ownerPrice);
    setTiersLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const { data: smart } = await api.post("/offers/smart-recommend", {
          arv:                    Number(propInfo.arv),
          living_area_sqft:       Number(propInfo.sqft) || 0,
          repair_cost_per_sqft:   25,
          // Only pass repairs_override if we don't have a chip — chip drives repairs otherwise
          repairs_override:       conditionKey ? null : (propInfo.repairs_estimate || null),
          days_on_market:         0,
          equity_pct:             Number(propInfo.equity_pct) || 100,
          mortgage_balance:       Number(propInfo.mortgage_balance) || 0,
          motivation_score:       Number(propInfo.motivation_score) || 50,
          investor_demand:        "med",
          zip_avg_assignment_fee: 15000,
          property_id:            propInfo.property_id || null,
          owner_price:            opNum > 0 ? opNum : null,
          condition_key:          conditionKey || null,
        });
        if (cancelled) {
          // eslint-disable-next-line no-console
          console.log("[smart-offer] response IGNORED (cancelled)", { conditionKey });
          return;
        }
        // eslint-disable-next-line no-console
        console.log("[smart-offer] response applied", {
          conditionKey,
          repairs: smart.repairs,
          tiers: (smart.tiers || []).map(t => `${t.key}=$${Math.round(t.price)}`).join(" · "),
        });
        setTiers(smart);
        // Auto-select the QUICK tier by default (60%) — user policy: reps
        // should anchor low first and only step up if the seller pushes back.
        const quick = (smart.tiers || []).find((t) => t.key === "quick");
        const rec   = (smart.tiers || []).find((t) => t.key === "recommended");
        const pickedTier = quick || rec;
        if (pickedTier?.price) {
          setOfferPrice(String(Math.round(pickedTier.price)));
          setSelectedTier(pickedTier.key);
        }
        // Confirm to the rep that the chip actually reshuffled the numbers —
        // helps debug "prices don't change" reports at a glance.
        if (conditionKey) {
          const repairsFmt = `$${Math.round(smart.repairs || 0).toLocaleString()}`;
          toast.success(`${conditionKey.toUpperCase()} → repairs ${repairsFmt} · Quick $${Math.round(quick?.price || 0).toLocaleString()}`,
                        { duration: 2200 });
        }
      } catch (e) {
        if (!cancelled) {
          const detail = e?.response?.data?.detail || e?.message || "Recalculation failed";
          toast.error(`Offer recalc: ${detail}`);
        }
      }
      finally { if (!cancelled) setTiersLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [propInfo, ownerPrice, conditionKey]);

  const sendOfferNow = async () => {
    if (!current || !propInfo) return;
    const price = parseFloat(offerPrice);
    if (!price || price <= 0) return toast.error("Enter a valid offer amount");
    if (!sellerEmail || !sellerEmail.includes("@")) {
      return toast.error("Seller email required to send for signature");
    }
    setSendingOffer(true);
    try {
      const { data } = await api.post("/dialer/send-offer", {
        lead_id: current.id,
        purchase_price: price,
        seller_email: sellerEmail,
        test_mode: false,   // always LIVE — real send
        tier_key: selectedTier,   // Tier History — analytics on which tier converts
      });
      toast.success(
        `Offer sent via ${data.provider || "signwell"}. Seller will get the signing email in seconds.`,
        { duration: 6000 },
      );
      setOfferSentThisCall(true);   // Tier Nudge — don't offer follow-up SMS if we already sent the contract
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send offer");
    } finally {
      setSendingOffer(false);
    }
  };

  // ============================================================ scripts + VMs
  const loadScripts = useCallback(async () => {
    try {
      const { data } = await api.get("/dialer/scripts");
      setScripts(data.scripts || []);
      const def = (data.scripts || []).find((s) => s.is_default);
      setActiveScript((prev) => prev || def || (data.scripts || [])[0] || null);
    } catch (_) { /* ignore */ }
  }, []);
  const loadVoicemails = useCallback(async () => {
    try {
      const { data } = await api.get("/dialer/voicemails");
      setVoicemails(data.voicemails || []);
    } catch (_) { /* ignore */ }
  }, []);
  useEffect(() => { loadScripts(); loadVoicemails(); }, [loadScripts, loadVoicemails]);

  // Replace {name}/{address}/{phone}/{city}/{state} placeholders with the
  // currently-dialed lead's values so the script reads naturally.
  const renderedScript = (body) => {
    if (!body || !current) return body || "";
    return body
      .replace(/\{name\}/g, current.name || "there")
      .replace(/\{address\}/g, current.property_address || "your property")
      .replace(/\{phone\}/g, current.phone || "")
      .replace(/\{city\}/g, current.city || "")
      .replace(/\{state\}/g, current.state || "");
  };

  // ============================================================ device init
  const initDevice = useCallback(async () => {
    if (deviceRef.current) return; // idempotent
    // Prompt for microphone up-front so we surface the browser permission
    // dialog BEFORE Twilio tries to grab audio (avoids buried 31401 errors).
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Immediately release the tracks — Twilio grabs its own stream on connect.
      stream.getTracks().forEach((t) => t.stop());
    } catch (e) {
      const isDenied = e && (e.name === "NotAllowedError" || e.name === "PermissionDeniedError");
      toast.error(
        isDenied
          ? "Microphone blocked. Click the 🔒 lock icon in the address bar → Microphone → Allow, then reload."
          : `Microphone unavailable: ${e?.message || "no input device"}`,
        { duration: 8000 },
      );
      setDeviceState("mic-blocked");
      return;
    }
    try {
      const { data } = await api.post("/dialer/token", {});
      const device = new Device(data.token, {
        logLevel: 1,
        codecPreferences: ["opus", "pcmu"],
        allowIncomingWhileBusy: false,
      });
      device.on("registered",   () => { setDeviceState("ready"); toast.success("Dialer ready"); });
      device.on("unregistered", () => {
        setDeviceState("unregistered");
        // Auto-heal: if the device silently unregisters (idle timeout,
        // token expiry that outran our refresh, or a sleeping laptop),
        // rebuild it in the background so the next call doesn't require
        // a manual page refresh.
        setTimeout(() => {
          if (deviceRef.current === device) {
            try { device.destroy(); } catch (_) { /* ignore */ }
            deviceRef.current = null;
            initDevice().catch(() => {});
          }
        }, 800);
      });
      device.on("error",        (e) => {
        setDeviceState("error");
        // 31005 = WebSocket connection to Twilio dropped. Auto-heal by
        // re-registering the device with a fresh token so the next call
        // doesn't inherit a dead socket. This is the #1 cause of the
        // dreaded "Call: ConnectionError (31005): Error sent from gateway
        // in HANGUP" seen in production when a call is placed after the
        // laptop wakes from sleep or the network flickered.
        if (e?.code === 31005 || /31005|gateway.*hangup/i.test(e?.message || "")) {
          toast.error("Twilio connection dropped — reconnecting…");
          // CRITICAL: destroy the dead device AND null the ref before
          // initDevice runs, otherwise its idempotent guard bails early
          // and the softphone stays "unregistered" forever.
          try { deviceRef.current?.destroy(); } catch (_) { /* ignore */ }
          deviceRef.current = null;
          setTimeout(() => { initDevice().catch(() => {}); }, 500);
          return;
        }
        toast.error(`Device: ${e.message}`);
      });
      device.on("tokenWillExpire", async () => {
        // SignalWire SATs are minted per-session; we re-register the device
        // rather than in-place token swap (which the Twilio SDK did).
        try {
          try { device.destroy(); } catch (_) { /* ignore */ }
          deviceRef.current = null;
          setTimeout(() => { initDevice().catch(() => {}); }, 500);
        } catch (_) { /* ignore transient */ }
      });
      device.on("incoming", (call) => {
        toast.info(`Incoming call from ${call.parameters.From}`);
        callRef.current = call;
        setCallState("ringing");
      });
      await device.register();
      deviceRef.current = device;
    } catch (e) {
      // Surface the exact SIP failure so we can debug instead of guessing.
      // The softphone shim now throws messages like
      //   "SIP WSS handshake failed → wss://…: …"
      // or "SIP REGISTER rejected (…): …" — those tell us exactly which
      // step is blocked (transport vs auth).
      const msg = e?.message || e?.response?.data?.detail || "Failed to init dialer";
      console.error("[Dialer] initDevice error:", e);
      toast.error(msg, { duration: 8000 });
    }
  }, []);

  // ============================================================ tear down
  useEffect(() => {
    return () => {
      if (tokenTimerRef.current) clearInterval(tokenTimerRef.current);
      try { deviceRef.current?.destroy(); } catch (_err) { /* ignore */ }
    };
  }, []);

  // ============================================================ dial
  const dialLead = async (lead, opts = {}) => {
    // Only reset to "single" when this call is a fresh manual entry.
    // The power-dial auto-advance passes `{ keepMode: true }` so its
    // callModeRef stays "power" through the whole queue — otherwise
    // every auto-dialed lead flipped the ref back to "single" and the
    // NEXT hangup skipped `dialNext`, stalling the queue after 2 calls.
    if (!opts.keepMode) callModeRef.current = "single";
    // ---- DIAGNOSTIC INSTRUMENTATION (temporary — remove after root cause is found) ----
    console.log("[FRONTEND] dial clicked", { source: "dialLead", ts: new Date().toISOString() });
    console.log("[FRONTEND] selected lead", { id: lead?.id, name: lead?.name, phone: lead?.phone });
    console.log("[FRONTEND] API base URL", {
      REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
      axios_baseURL: api?.defaults?.baseURL,
      window_origin: (typeof window !== "undefined") ? window.location.origin : "n/a",
      window_href: (typeof window !== "undefined") ? window.location.href : "n/a",
    });
    console.log("[FRONTEND] deviceRef present?", !!deviceRef.current, "deviceState:", deviceState);
    if (!deviceRef.current) {
      console.log("[FRONTEND] deviceRef missing → calling initDevice() first");
      try { await initDevice(); }
      catch (initErr) { console.error("[FRONTEND] initDevice error inside dialLead", initErr); }
      console.log("[FRONTEND] initDevice() returned. deviceRef present?", !!deviceRef.current);
    }
    const to = e164(lead.phone);
    console.log("[FRONTEND] destination (E.164)", { raw: lead?.phone, e164: to });
    if (!to) {
      console.warn("[FRONTEND] aborting — bad phone number");
      return toast.error("Bad phone number on this lead");
    }

    // If we're switching to a NEW lead while state got stuck (e.g. rep
    // closed the dispo dialog), reset callState so the softphone can dial
    // again without a page refresh.
    if (callState !== "idle" && current?.id !== lead.id) {
      setCallState("idle");
    }

    // Try to acquire the soft call-lock BEFORE connecting. If someone
    // else in the same account/tenant is already dialing this lead, we bail
    // out cleanly so the two reps never talk to the same seller at once.
    // Skip locking for synthetic manual-dial IDs (they don't exist in CRM).
    const isSyntheticLead = String(lead.id || "").startsWith("manual-");
    // ---- SPEED FIX (owner ask 2026-08-26): fire lock + pick-caller-id
    // in PARALLEL so we save ~100-200ms per dial. Both are DB-only and
    // fully independent — no reason to await sequentially.
    const dialT0 = Date.now();
    let callerId = status.caller_id_pool[0];
    const [lockRes, pickRes] = await Promise.allSettled([
      isSyntheticLead
        ? Promise.resolve({ data: { ok: true, synthetic: true } })
        : api.post(`/dialer/lock/${lead.id}`, { session_token: sessionTokenRef.current }),
      api.get("/dialer/pick-caller-id", { params: { to } }),
    ]);
    if (lockRes.status === "rejected") {
      const e = lockRes.reason;
      if (e?.response?.status === 409) {
        toast.error("Another rep is already on this lead. Grabbing the next one.", { duration: 4000 });
        loadQueue();
        return;
      }
      // Any other error — non-fatal, keep dialing.
    } else if (lockRes.value?.data?.ok && !isSyntheticLead) {
      activeLockRef.current = lead.id;
    }
    if (pickRes.status === "fulfilled" && pickRes.value?.data?.caller_id) {
      callerId = pickRes.value.data.caller_id;
    }
    console.log(`[SPEED-DIAL] lock+pick parallel elapsed_ms=${Date.now() - dialT0}`);
    setCurrent(lead);
    setNotes("");
    setCallState("connecting");
    setActiveCallerId(callerId || "");   // remembered for VM / SMS drops

    try {
      // ─── Step 1: create a server-side call intent so the LaML
      // webhook knows this browser INVITE is real. See
      // /app/backend/call_intent_service.py for the state machine.
      console.log("[FRONTEND] starting backend request", {
        endpoint: "POST /api/dialer/prepare",
        mode: isSyntheticLead ? "manual" : "single",
        destination: to,
        caller_id: callerId,
        lead_id: lead.id,
      });
      const { data: intent } = await api.post("/dialer/prepare", {
        mode: isSyntheticLead ? "manual" : "single",
        destination: to,
        caller_id: callerId,
        lead_id: isSyntheticLead ? null : lead.id,
      });
      console.log("[FRONTEND] backend response (prepare)", intent);
      const intentId = intent.intent_id;
      const confName = intent.conference_name;

      // ─── Step 2: browser INVITE joins the conference. LaML webhook
      // atomically claims the pending intent by SIP username and
      // returns <Dial><Conference> LaML that bridges the browser leg.
      console.log("[FRONTEND] calling SIP connect", { conf: confName, intentId });
      const call = await deviceRef.current.connect({ params: { conf: confName } });
      console.log("[FRONTEND] SIP connect returned", { hasCall: !!call });
      callRef.current = call;
      currentIntentIdRef.current = intentId;

      let audioAccepted = false;
      let originateFired = false;
      let audioTimeoutId = null;
      setMediaStatus("joining");
      audioTimeoutId = setTimeout(() => {
        if (!audioAccepted) {
          setMediaStatus("failed");
          toast.error("Audio failed to connect — customer may hear silence. End the call and try again.", { duration: 10000 });
        }
      }, 20000);

      call.on("accept", async (c) => {
        audioAccepted = true;
        clearTimeout(audioTimeoutId);
        console.log("[CONF] browser joined", { intentId, callSid: c?.parameters?.CallSid });
        setCallState("connected");
        setMediaStatus("connected");
        setCallStartedAt(Date.now());
        setCallSid(c?.parameters?.CallSid || null);
        // ─── Step 3: NOW originate the PSTN leg into the same conference.
        // Guarded by originateFired so accidental double-fire is a no-op
        // (backend also enforces idempotency via mark_pstn_originating).
        if (originateFired) return;
        originateFired = true;
        try {
          console.log("[FRONTEND] starting backend request", { endpoint: `POST /api/dialer/originate/${intentId}` });
          const { data: orig } = await api.post(`/dialer/originate/${intentId}`);
          console.log("[PSTN] originate response", orig);
        } catch (originErr) {
          console.error("[FRONTEND] originate failed", {
            message: originErr?.message,
            status: originErr?.response?.status,
            data: originErr?.response?.data,
          });
          toast.error(originErr?.response?.data?.detail || "Couldn't dial the customer — check backend logs");
        }
      });
      call.on("ringing", () => { setCallState("ringing"); setMediaStatus("joining"); });
      call.on("mute", (m) => setMuted(m));
      call.on("disconnect", async () => {
        clearTimeout(audioTimeoutId);
        setCallState("ended");
        if (audioAccepted) {
          setMediaStatus("ended");
          setShowDispo(true);
        } else {
          setMediaStatus("failed");
          // ── Auto-skip bad numbers (owner ask 2026-08-25 P1). If the
          // PSTN leg was rejected by the carrier as unroutable/unallocated
          // (SIP 404/484/603) the backend has already stamped
          // last_call_fail_reason="bad_number" on the lead. Instead of
          // making the rep click Wrong # we auto-save the disposition
          // and let the queue advance to the next lead.
          try {
            const leadId = current?.id;
            if (leadId) {
              const { data: st } = await api.get(`/dialer/lead/${leadId}/last-call-status`);
              if (st?.fail_reason === "bad_number") {
                toast.error(`Bad number (SIP ${st.sip_code}) — auto-skipping`, { duration: 4000 });
                // saveDisposition() picks up `current` from state and
                // triggers the normal wrong-number → swap-phone → advance
                // flow. Kept identical to the manual click path so the
                // recycle / retrace UX is unchanged.
                await saveDisposition("wrong-number");
                return;
              }
            }
          } catch (_) { /* fall through to Redial */ }
          // Don't open the dispo modal — nothing happened worth
          // disposing. Rep can Redial once network stabilizes.
        }
      });
      call.on("cancel", () => {
        clearTimeout(audioTimeoutId);
        setCallState("ended");
        setMediaStatus(audioAccepted ? "ended" : "failed");
        if (audioAccepted) setShowDispo(true);
      });
      call.on("reject", () => {
        clearTimeout(audioTimeoutId);
        setCallState("ended");
        setMediaStatus(audioAccepted ? "ended" : "failed");
        if (audioAccepted) setShowDispo(true);
      });
      call.on("error", async (e) => {
        // 31005 = Twilio Voice gateway hangup. Almost always transient —
        // caused by WebSocket flicker, laptop sleep, tab-swap, or the
        // browser losing UDP briefly. Auto-heal: re-register the device
        // with a fresh token, then let the user hit Call again.
        if (e?.code === 31005 || /31005|gateway.*hangup/i.test(e?.message || "")) {
          toast.error("Twilio connection dropped — reconnecting the dialer…", {
            description: "Hit Call again in a moment. This is a browser/network issue, not a bug.",
            duration: 5000,
          });
          try { deviceRef.current?.destroy(); } catch (_) { /* ignore */ }
          deviceRef.current = null;
          setTimeout(() => { initDevice().catch(() => {}); }, 500);
          return;
        }
        toast.error(`Call: ${e.message || e.code}`);
        // Something errored out before we ever connected — free the lock so
        // another rep can pick this lead up right away.
        if (activeLockRef.current) {
          const lid = activeLockRef.current;
          activeLockRef.current = null;
          api.post(`/dialer/unlock/${lid}`, { session_token: sessionTokenRef.current })
             .catch(() => {});
        }
      });
    } catch (e) {
      console.error("[FRONTEND] error (dialLead outer catch — SIP connect or setup failed)", {
        message: e?.message,
        code: e?.code,
        stack: e?.stack,
        status: e?.response?.status,
        data: e?.response?.data,
      });
      toast.error("Failed to start call");
      setCallState("idle");
      setCurrent(null);
    }
  };

  // End Call — comprehensive teardown that also opens the disposition modal.
  //
  // Previously `hangUp` was just `callRef.current?.disconnect()`, which:
  //   • Left racing PSTN legs alive when in a Power Dial session
  //     (browser leg died but SignalWire kept dialing the other lines).
  //   • Only opened the dispo modal via the `disconnect` event handler,
  //     which is gated on `audioAccepted` — so ending a ringing/
  //     connecting call skipped the dispo modal entirely and the rep
  //     never got to log "wrong-number" / "no-answer".
  //
  // New behavior (2026-08-18 user requests #1 + #3):
  //   1. Open the dispo modal IMMEDIATELY if there's a current lead —
  //      lets the rep start categorizing while we tear down audio.
  //   2. If a Power Dial session is running, cancel it server-side so
  //      ALL racing PSTN legs die, then stop the polling loop.
  //   3. Disconnect the current browser call AND the underlying device
  //      (belt-and-suspenders — prevents ghost legs).
  //   4. Reset the local call state so a fresh dial can start clean.
  //   5. On dispo submit, the existing autoAdvance path (line ~1152)
  //      queues the next lead. No changes needed there.
  const hangUp = async () => {
    // Open dispo modal ASAP so the rep can pick an outcome without
    // waiting for network teardown. Only if we actually have a
    // real lead — synthetic manual dials skip dispo (there's no CRM
    // record to write against).
    if (current && !String(current.id || "").startsWith("manual-")) {
      setShowDispo(true);
    }
    // Cancel the power-dial session (kills all racing PSTN legs).
    if (powerSession?.session_id) {
      stopPowerPolling();
      try {
        await api.post(`/power-dial/cancel?session_id=${powerSession.session_id}`);
      } catch (_) { /* best effort — the leg cleanup below is the real safeguard */ }
      setPowerSession(null);
    }
    // Tear down every browser leg. Uses BOTH the current-call disconnect
    // and Device.disconnectAll() because .disconnect() on a wrapped call
    // that's still in "connecting" state can leave the underlying UA
    // dangling.
    try { callRef.current?.disconnect(); } catch (_) { /* ignore */ }
    try { deviceRef.current?.disconnectAll(); } catch (_) { /* ignore */ }
    callRef.current = null;
    // Reset audio-related state so the softphone UI reflects idle.
    setCallState("ended");
    setMuted(false);
  };
  const toggleMute = () => callRef.current && callRef.current.mute(!muted);
  const dtmf     = (d) => callRef.current?.sendDigits(d);

  // ============================================================ auto-dial
  // Deep-link support:
  //   /dialer?leadId=<id>                             → fetch lead, dial it
  //   /dialer?phone=<E.164>&name=<..>&address=<..>    → synthetic dial (no CRM lead)
  // Runs exactly once per navigation. `dialLead()` internally awaits
  // `initDevice()` (which awaits SIP REGISTER 200-OK), so the pending
  // call automatically fires once the softphone is Ready — the rep
  // does NOT need to click Call again.
  useEffect(() => {
    if (autoDialTriedRef.current) return;
    if (!autoDialLeadId && !autoDialPhone) return;
    if (callState !== "idle") return;
    autoDialTriedRef.current = true;
    (async () => {
      try {
        let lead = null;
        if (autoDialLeadId) {
          const { data } = await api.get(`/leads/${autoDialLeadId}`);
          lead = data;
          if (!lead?.phone) {
            toast.error("This lead has no phone number — skip trace it first");
            setSearchParams({}, { replace: true });
            return;
          }
        } else {
          // Synthetic dial: no CRM lead, phone came from PropertyLookup /
          // Pipeline / CallSmsDialog. Merge stashed context (seller name,
          // property address) so the customer card renders immediately.
          let stashed = {};
          try {
            const raw = localStorage.getItem("dialer.pendingCall");
            if (raw) stashed = JSON.parse(raw) || {};
          } catch { /* ignore */ }
          const phone = autoDialPhone;
          lead = {
            id:               `manual-${Date.now()}`,
            phone,
            name:             autoDialName    || stashed.seller_name      || "Manual dial",
            property_address: autoDialAddress || stashed.property_address || null,
          };
        }
        setCurrent(lead);
        setNotes(lead.notes || "");
        toast.info(`Dialing ${lead.name || lead.phone}…`);
        try { localStorage.removeItem("dialer.pendingCall"); } catch { /* ignore */ }
        await dialLead(lead);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load the lead to auto-dial");
      } finally {
        setSearchParams({}, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDialLeadId, autoDialPhone, callState]);

  // ---- Tier Nudge: fire a follow-up SMS quoting the viewed tier's price ----
  // Called from saveDisposition when the agent picked "voicemail" / "no-answer"
  // / "callback" and never actually sent the offer during the call.
  //
  // DOUBLE-SEND GUARD (2026-08-21): `nudgeSendingRef` + optimistic
  // `setOfferSentThisCall(true)` BEFORE the await block. Reps saw the
  // same SMS land twice ~1s apart in prod — traced to saveDisposition
  // being called back-to-back (double click / React StrictMode double
  // invoke). The optimistic flag flips synchronously so a second call
  // now short-circuits at the caller-site guard on Dialer.jsx:1167.
  const nudgeSendingRef = useRef(false);
  const sendTierNudgeSms = async () => {
    if (nudgeSendingRef.current) return;
    if (!current?.phone) return;
    const tier = tiers?.tiers?.find((t) => t.key === selectedTier);
    if (!tier?.price) return;
    nudgeSendingRef.current = true;
    setOfferSentThisCall(true);       // flip synchronously so a re-entrant call short-circuits
    // 2026-08-21: NEVER pipe the lead's phone number back into the SMS
    // body as the greeting — reps reported "Hi +19529378281 …" going out
    // when the CSV import didn't capture an owner name. Reject phone-
    // shaped values before falling through to "Neighbor".
    const rawName = String(current.name || "").trim();
    const looksLikeNumber = /^[+\d\s()\-]+$/.test(rawName);
    const cleanName = (looksLikeNumber || rawName.toLowerCase().startsWith("unknown"))
                        ? ""
                        : rawName;
    const firstName = cleanName.split(" ")[0] || "Neighbor";
    const addr = current.property_address || propInfo?.address || "your property";
    const priceStr = `$${Math.round(tier.price).toLocaleString()}`;
    const body =
      `Hi ${firstName}, this is ${user?.name || "Flip Pilot"}. I was following ` +
      `up about the property at ${addr}.\n` +
      `We're currently buying a few properties in the area and wanted to see ` +
      `if you'd be open to receiving a cash offer of ${priceStr} for yours.\n` +
      `There's no need to make repairs, no commissions or agent fees, and we ` +
      `can close in 30 days or less, depending on what works best for you.\n` +
      `Would you be open to hearing an offer? Reply YES / NO / CALL and I'll ` +
      `take it from there.`;
    try {
      await api.post("/twilio/sms/send", {
        to:      current.phone,
        body,
        lead_id: current.id,
      });
      toast.success(`Sent follow-up SMS quoting ${priceStr}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "SMS send failed";
      toast.error(detail);
      setOfferSentThisCall(false);    // rollback on failure so the rep can retry manually
    } finally {
      nudgeSendingRef.current = false;
    }
  };

  // Belt-and-suspenders guard: even if the seat-of-the-pants rep triple-
  // clicks a dispo chip or React StrictMode double-invokes the handler,
  // `savingDispoRef.current` short-circuits every entry after the first
  // until the backend + auto-advance finish. Combined with the
  // `nudgeSendingRef` inside `sendTierNudgeSms` this makes it impossible
  // for the same follow-up SMS to fire more than once per call.
  const savingDispoRef = useRef(false);
  const [savingDispo, setSavingDispo] = useState(false);

  const saveDisposition = async (dispoKey) => {
    if (savingDispoRef.current) return;
    if (!current) return;
    // Callback needs a datetime — show inline picker first
    if (dispoKey === "callback" && !callbackAt) {
      setDispoChoice("callback");
      return;
    }
    savingDispoRef.current = true;
    setSavingDispo(true);
    // Compute talk-time from callStartedAt → now (in seconds). This
    // powers the "Talk" cell in the Today Bar. Falls back to 0 for
    // no-answer / voicemail flows where the call never connected.
    const talkSecs = callStartedAt
      ? Math.max(0, Math.round((Date.now() - callStartedAt) / 1000))
      : 0;
    // ---- Speed Dialer Time-Between-Calls (owner ask 2026-08-26) ----
    // Rep experience target: talk → dispo → next ringing within 1-2s.
    // We mark the exact moment the dispo submit was clicked, then
    // measure again when the next dialLead() actually fires so we can
    // surface any regression in the "gap" telemetry.
    const dispoSubmittedAt = Date.now();
    dispoSubmittedAtRef.current = dispoSubmittedAt;
    try {
      const { data: dispoResp } = await api.post("/dialer/disposition", {
        call_sid: callSid || "unknown",
        lead_id:  current.id,
        disposition: dispoKey,
        notes:    notes || "",
        mark_dnc: dispoKey === "dnc",
        callback_at: dispoKey === "callback" ? callbackAt : null,
        duration_seconds: talkSecs,
      });
      const dispoDoneAt = Date.now();
      const backendMs   = dispoResp?.elapsed_ms || 0;
      const networkMs   = dispoDoneAt - dispoSubmittedAt;
      console.info(
        `[SPEED-DIAL] dispo saved dispo=${dispoKey} backend_ms=${backendMs} network_ms=${networkMs}`
      );
      toast.success(
        dispoKey === "market-value"
          ? "Saved & moved to Market Value Seller board"
          : `Saved as ${dispoKey}`
      );

      // Refresh Today Bar in background — never awaited.
      api.get("/dialer/rep-stats-today")
        .then(({ data }) => setRepStats(data))
        .catch(() => {});

      // Server already released call-lock — clear our ref.
      activeLockRef.current = null;

      // Tier Nudge SMS → FIRE AND FORGET (was blocking ~800ms).
      const SOFT = ["voicemail", "no-answer", "callback"];
      if (
        tierNudgeOptIn && !offerSentThisCall &&
        SOFT.includes(dispoKey) &&
        tiers?.tiers?.find((t) => t.key === selectedTier)?.price &&
        current?.phone
      ) {
        sendTierNudgeSms().catch((e) => console.warn("tier nudge failed", e));
      }
    } catch (e) {
      toast.error("Failed to save disposition");
      savingDispoRef.current = false;
      setSavingDispo(false);
      return;
    }
    // No-answer / voicemail → recycle the lead to the BOTTOM of the queue
    // so we retry it at the end of the session instead of skipping it.
    // Wrong-number → try to SWAP to the next cached phone; if we found one,
    // move to bottom (retry with new phone), else drop from queue.
    // Everything else (talked, DNC, market-value, etc.) → drop from queue.
    const RECYCLE = new Set(["no-answer", "voicemail"]);
    let remaining;
    if (dispoKey === "wrong-number") {
      try {
        const { data } = await api.post(`/leads/${current.id}/swap-next-phone`);
        if (data.swapped && data.new_phone) {
          // Swap succeeded — update the current lead in-place with the new
          // phone number, then push it to the bottom of the queue for a retry.
          const updated = { ...current, phone: data.new_phone };
          const others = queue.filter((l) => l.id !== current.id);
          remaining = [...others, updated];
          toast.success(
            `Bad number logged. Trying ${data.new_phone} on next pass (${data.remaining_alternates} more alts on file)`,
            { duration: 5000 }
          );
        } else {
          // No more alternates — drop the lead, offer a one-click re-trace
          remaining = queue.filter((l) => l.id !== current.id);
          const leadForRetrace = current;   // capture before we clear state
          toast("No more phones on file for this lead", {
            description: "Wanna pull fresh numbers from the skip-trace provider?",
            duration: 8000,
            action: {
              label: "Refresh skip-trace",
              onClick: async () => {
                try {
                  const { data } = await api.post("/skip-trace", {
                    address: leadForRetrace.property_address || "",
                    owner_name: leadForRetrace.name || "",
                    lead_id: leadForRetrace.id,
                  });
                  const found = (data.phones || []).length;
                  if (found > 0) {
                    toast.success(`Found ${found} new phone${found === 1 ? "" : "s"} — added back to the queue`);
                    // Reload the queue so the freshly-enriched lead reappears
                    loadQueue();
                  } else {
                    toast.info("No new numbers found — try Apify or wait 30 days");
                  }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Skip-trace failed");
                }
              },
            },
          });
        }
      } catch (_) {
        remaining = queue.filter((l) => l.id !== current.id);
      }
    } else if (["no-answer", "voicemail", "busy"].includes(dispoKey) && !current._triedAlt) {
      // Multi-phone rotation: primary didn't pick up → try the lead's
      // secondary phone (max 2 per lead) BEFORE moving to the next
      // contact. `_triedAlt` locks the swap to happen only once per
      // lead — if secondary also misses, we fall into the normal
      // RECYCLE / drop path on the next dispo.
      try {
        const { data } = await api.post(`/leads/${current.id}/swap-next-phone`);
        if (data.swapped && data.new_phone) {
          const updated = { ...current, phone: data.new_phone, _triedAlt: true };
          const others = queue.filter((l) => l.id !== current.id);
          remaining = [updated, ...others];   // TOP so power-dial hits it next
          toast.info(`No answer on primary — trying alt ${data.new_phone}`);
        } else {
          // No alternate on file — treat as normal recycle to bottom.
          const others = queue.filter((l) => l.id !== current.id);
          remaining = RECYCLE.has(dispoKey) ? [...others, current] : others;
        }
      } catch (_) {
        remaining = queue.filter((l) => l.id !== current.id);
      }
    } else if (RECYCLE.has(dispoKey)) {
      const others = queue.filter((l) => l.id !== current.id);
      remaining = [...others, current];   // current lead pushed to bottom
      toast.info("Moved to bottom of the queue — we'll try again later");
    } else {
      remaining = queue.filter((l) => l.id !== current.id);
    }
    setQueue(remaining);
    setShowDispo(false);
    setCurrent(null);
    setCallState("idle");
    setNotes("");
    setCallSid(null);
    setDispoChoice(null);
    setCallbackAt("");

    if (autoAdvance && remaining.length > 0) {
      // Grab whichever lead is at the top now.
      const next = remaining[0];
      if (next.id !== current.id) {
        // ZERO-DELAY auto-advance (owner ask 2026-08-25 P3): as soon as
        // the rep saves ANY disposition, the next queue lead dials
        // synchronously. Works from EVERY mode — single, power, manual —
        // as long as `autoAdvance` is on and the queue has more leads.
        callModeRef.current = "power";
        // ---- SPEED-DIAL TIME-BETWEEN-CALLS TELEMETRY ----
        // Measure the ACTUAL gap the rep experiences: dispo click →
        // next call dial request. Anything over 5s logs a warning so
        // regressions get surfaced in prod logs.
        const gapMs = Date.now() - (dispoSubmittedAtRef.current || Date.now());
        if (gapMs > 5000) {
          console.warn(`[SPEED-DIAL] SLOW gap=${gapMs}ms — investigate`);
        } else {
          console.info(`[SPEED-DIAL] talk → dispo → next-dial gap=${gapMs}ms (target ≤2000ms)`);
        }
        // Respect admin-configured inter-call gap (default 500ms). 0 = fire immediately.
        const gap = Math.max(0, Number(dialerPrefsRef.current.inter_call_gap_ms) || 0);
        const fire = () => {
          dialLead(next, { keepMode: true }).catch((err) => {
            console.error("[AUTO-ADVANCE] dialLead failed", err);
          });
        };
        if (gap === 0) fire();
        else setTimeout(fire, gap);
      }
    }
    // Clear the re-entry lock now that saveDisposition has fully wound
    // down (including auto-advance queueing). A rep can trigger the next
    // dispo — the guard only ever blocked concurrent invocations of the
    // SAME dispo save.
    savingDispoRef.current = false;
    setSavingDispo(false);
  };

  const dropVoicemail = async (vmId) => {
    if (!callSid) return toast.error("No active call to drop into");
    setDroppingVm(true);
    try {
      await api.post("/dialer/drop-voicemail", {
        call_sid:     callSid,
        voicemail_id: vmId,
        lead_id:      current?.id || null,
        caller_id:    activeCallerId || null,
      });
      toast.success("Voicemail dropping — you're free to move on");
      // Twilio will release our (agent) leg once redirected; disconnect UI
      setTimeout(() => callRef.current?.disconnect(), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "VM drop failed");
    } finally {
      setDroppingVm(false);
    }
  };

  const manualDial = () => {
    console.log("[FRONTEND] dial clicked", { source: "manualDial", raw: manualNumber, ts: new Date().toISOString() });
    const to = e164(manualNumber);
    console.log("[FRONTEND] destination (E.164)", { raw: manualNumber, e164: to });
    if (!to) {
      console.warn("[FRONTEND] aborting manualDial — invalid US phone");
      return toast.error("Enter a valid US phone (10 digits)");
    }
    const fakeLead = { id: `manual-${Date.now()}`, name: "Manual dial", phone: to };
    console.log("[FRONTEND] selected lead (manual)", fakeLead);
    dialLead(fakeLead);
    setManualNumber("");
  };

  const skipLead = () => {
    setQueue((q) => q.filter((l) => l.id !== current?.id));
    setShowDispo(false); setCurrent(null); setCallState("idle");
  };

  // ============================================================ Power Dialer
  // Fetch compliance stats on mount and after every session ends
  const loadCompliance = useCallback(async () => {
    try {
      const { data } = await api.get("/power-dial/compliance", { params: { days: 30 } });
      setCompliance(data);
    } catch (_) { /* ignore — badge just won't render */ }
  }, []);
  useEffect(() => { loadCompliance(); }, [loadCompliance]);

  // Cleanup poller on unmount
  useEffect(() => () => {
    if (powerPollRef.current) clearInterval(powerPollRef.current);
  }, []);

  const stopPowerPolling = () => {
    if (powerPollRef.current) {
      clearInterval(powerPollRef.current);
      powerPollRef.current = null;
    }
  };

  const startPowerDial = async () => {
    // 2026-08-21: Single-line Auto-Dial engine. Rep clicks Start once,
    // and the queue auto-continues until every lead is dispositioned.
    // Sets `callModeRef="power"` so saveDisposition's autoAdvance hook
    // (Dialer.jsx:1282) fires the next dialLead() the instant a dispo
    // is saved — no idle window between calls. NO multi-line prepare,
    // NO predictive restart, NO winner-picking.
    callModeRef.current = "power";
    if (powerBusy) return;
    if (deviceState !== "ready") {
      toast.error("Connect the dialer first (grant mic access, wait for the green pill)");
      return;
    }
    if (callState !== "idle" && callState !== "ended") {
      toast.error(`Finish the current call first (state: ${callState})`);
      return;
    }
    if (callState === "ended") {
      setCurrent(null);
      setCallSid(null);
      setCallStartedAt(null);
      setShowDispo(false);
      setCallState("idle");
    }
    // Grab the first dialable lead
    const first = queue.find((l) => {
      const d = String(l.phone || "").replace(/\D/g, "");
      return d.length >= 10;
    });
    if (!first) {
      toast.error("No dialable leads in the queue");
      return;
    }
    setPowerBusy(true);
    try {
      // Best-effort single-lead lock so a teammate doesn't grab the same
      // lead mid-dial. Non-fatal if it fails — dialLead will surface any
      // hard conflict.
      try {
        await api.post("/dialer/lock-bulk", {
          session_token: sessionTokenRef.current,
          lead_ids: [first.id],
        });
      } catch { /* ignore */ }
      await dialLead(first, { keepMode: true });
      toast.success("Auto-Dial started — I'll roll into the next lead as soon as you dispo this one.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start Auto-Dial");
    } finally {
      setPowerBusy(false);
    }
  };

  const cancelPowerDial = async () => {
    if (!powerSession) return;
    stopPowerPolling();
    try {
      await api.post(`/power-dial/cancel?session_id=${powerSession.session_id}`);
    } catch (_) { /* ignore — best effort */ }
    // Full audio-layer cleanup. `disconnect()` on the wrapped call
    // sometimes leaves the underlying Device connection dangling when the
    // conference leg was still in "connecting" state — which is how we
    // ended up with a hidden mic-on tab and no way to hang up. Using
    // Device.disconnectAll() guarantees every browser leg is torn down.
    try { callRef.current?.disconnect(); } catch (_) { /* ignore */ }
    try { deviceRef.current?.disconnectAll(); } catch (_) { /* ignore */ }
    callRef.current = null;
    setPowerSession(null);
    setCurrent(null);
    setCallState("idle");
    setCallStartedAt(null);
    setCallSid(null);
    loadCompliance();
  };

  // ---- Emergency "Force End All Calls" ----
  // Nuke every possible in-flight call state:
  //   1. cancel any live power-dial session server-side (all conf legs die)
  //   2. Device.disconnectAll() — kills every browser-held leg
  //   3. current single-line call disconnect for good measure
  //   4. reset ALL React state so the softphone shows idle and unlocks
  // Rep uses this when the softphone UI shows "Pick a lead" but the browser
  // tab still has audio flowing (a Cancel-All that didn't clean up).
  const forceEndAllCalls = async () => {
    stopPowerPolling();
    if (powerSession?.session_id) {
      try {
        await api.post(`/power-dial/cancel?session_id=${powerSession.session_id}`);
      } catch (_) { /* best effort */ }
    }
    try { callRef.current?.disconnect(); } catch (_) { /* ignore */ }
    try { deviceRef.current?.disconnectAll(); } catch (_) { /* ignore */ }
    callRef.current = null;
    setPowerSession(null);
    setCurrent(null);
    setCallState("idle");
    setCallStartedAt(null);
    setCallSid(null);
    setMuted(false);
    setShowDispo(false);
    toast.success("All calls ended.");
    try { await loadQueue(); } catch (_) { /* ignore */ }
  };

  // 2026-08-21: Retired the multi-line predictive restart effect.
  // Single-line Auto-Dial advances via saveDisposition's autoAdvance
  // hook (Dialer.jsx:1282) — this effect is no longer needed. Any
  // stale `powerSession` still gets torn down defensively so the UI
  // doesn't wedge on an orphan session that survived a reload.
  useEffect(() => {
    if (callState === "ended" && powerSession) {
      stopPowerPolling();
      setPowerSession(null);
      loadCompliance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState, powerSession, loadCompliance]);

  // ---- Ghost-call detector ----
  // Every 2 seconds check whether the underlying Twilio Device is holding
  // an active connection while our React state thinks we're idle. This is
  // the "Cancel All left the browser leg connected" symptom — the softphone
  // shows "Pick a lead" but the browser tab has audio flowing. When we
  // detect the mismatch we flip a flag which shows an unmissable red
  // "Force End All Calls" button on the softphone card.
  const [ghostCall, setGhostCall] = useState(false);
  useEffect(() => {
    const iv = setInterval(() => {
      const dev = deviceRef.current;
      if (!dev) { setGhostCall(false); return; }
      let hasActive = false;
      try {
        if (typeof dev.isBusy === "function") {
          hasActive = dev.isBusy();
        } else if (typeof dev.calls !== "undefined") {
          hasActive = (dev.calls || []).length > 0;
        } else if (typeof dev.activeConnection === "function") {
          hasActive = !!dev.activeConnection();
        }
      } catch (_) { /* ignore probe errors */ }
      const uiIdle = callState === "idle" && !powerSession && !current;
      setGhostCall(hasActive && uiIdle);
    }, 2000);
    return () => clearInterval(iv);
  }, [callState, powerSession, current]);

  return (
    <div className="mx-auto p-4 md:p-6 lg:px-10 xl:px-14 2xl:px-20" data-testid="dialer-page">
      {/* ============================================================ HEADER */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Power Dialer</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Dialer</h1>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Badge variant="outline" className={`text-xs ${status.configured ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-red-300 bg-red-50 text-red-700"}`}>
            {status.configured ? "Connected" : "Not configured"}
          </Badge>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setManageOpen("scripts")}
                    className="rounded-full h-7 text-[11px]" data-testid="dialer-manage-scripts-btn">
              <BookText size={11} className="mr-1" /> Scripts ({scripts.length})
            </Button>
          </div>
          {deviceState !== "ready" && (
            <Button size="sm" onClick={initDevice} data-testid="dialer-init-btn"
                    className="rounded-full h-8 text-xs">
              <Phone size={12} className="mr-1.5" />
              {deviceState === "idle" ? "Connect dialer" :
               deviceState === "mic-blocked" ? "Retry (grant mic)" : deviceState}
            </Button>
          )}
          {deviceState === "ready" && (
            <Badge className="bg-emerald-600 hover:bg-emerald-600 text-xs">🎙 Dialer live</Badge>
          )}
          {/* Media-connection status pill — visible ONLY during an active
              call. Green means the browser leg fully joined the conference
              and audio is flowing. Amber "Joining audio…" means the SIP
              INVITE is negotiating; if this ever gets stuck (>20s) the
              rep sees red "Audio failed". Without this, silent failures
              would look identical to normal call flow and reps would
              advance to the next lead while the customer was still on
              the line hearing dead air (TCPA abandonment risk). */}
          {mediaStatus === "joining" && (
            <Badge className="bg-amber-500 hover:bg-amber-500 text-xs animate-pulse" data-testid="dialer-media-joining">
              <Loader2 size={11} className="animate-spin mr-1" />Joining audio…
            </Badge>
          )}
          {mediaStatus === "connected" && (
            <Badge className="bg-emerald-500 hover:bg-emerald-500 text-xs" data-testid="dialer-media-connected">
              🔊 Audio live
            </Badge>
          )}
          {mediaStatus === "failed" && (
            <button
              onClick={() => {
                toast.info("Reconnecting audio device…", { duration: 3000 });
                setMediaStatus(null);
                // Tear down the current device and re-register so the browser
                // negotiates a fresh WebRTC session. This clears most
                // "Audio failed to connect" cases (stale ICE / DTLS handshake).
                try { deviceRef.current?.dispose?.(); } catch (_) { /* ignore */ }
                deviceRef.current = null;
                setDeviceState("idle");
                setTimeout(() => {
                  initDevice()
                    .then(() => toast.success("Audio reconnected — safe to dial again"))
                    .catch(() => toast.error("Reconnect failed — reload the page or check mic permissions"));
                }, 400);
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-2.5 py-1 transition"
              data-testid="dialer-media-failed"
              title="Click to reconnect the softphone"
            >
              ⚠ Audio failed to connect · Fix
            </button>
          )}
        </div>
      </div>

      {/* ============================================================ TODAY BAR (Enzo parity) */}
      {/* Compact rep-stats strip: today's activity + live clock + a big
          Active Callbacks pill (like Enzo's "2 ACTIVE CALLBACKS" button).
          Owner ask 2026-08-28: Auto-Dial + Pause buttons live in this bar
          now (right beside Active Callbacks / NO LIVE CALL) so reps never
          scroll to start the queue. */}
      <div className="mt-4 rounded-2xl border border-[var(--app-line)] bg-white px-4 py-3 flex flex-wrap items-center gap-3"
           data-testid="dialer-today-bar">
        <div className="flex items-center gap-4 flex-wrap">
          <StatCell label="Total"    value={repStats.total} />
          <StatCell label="Outbound" value={repStats.outbound} tone="slate" />
          <StatCell label="Inbound"  value={repStats.inbound}  tone="sky" />
          <StatCell label="Sales"    value={repStats.sales}    tone="emerald" bold />
          <StatCell label="Close %"  value={`${repStats.close_rate_pct}%`} tone="violet" />
          <StatCell label="Talk"     value={repStats.avg_talk_time} tone="amber" />
        </div>
        <div className="flex-1" />

        {/* Auto-Dial + Pause (lifted from the queue card 2026-08-28) */}
        {!powerSession && (() => {
          const dialableCount = queue.filter((l) => String(l.phone || "").replace(/\D/g, "").length >= 10).length;
          const notReady = deviceState !== "ready";
          const notIdle  = callState !== "idle" && callState !== "ended";
          const noLeads  = dialableCount < 1;
          const blockedReason = powerBusy ? "Starting…"
                              : notReady  ? "Dialer not connected (grant mic access, wait for green)"
                              : notIdle   ? `Finish the current call first (state: ${callState})`
                              : noLeads   ? "No dialable leads in the queue (no phone numbers)"
                              : null;
          return (
            <Button
              size="sm"
              onClick={startPowerDial}
              disabled={powerBusy}
              title={blockedReason || `Auto-dial the queue — ${dialableCount} dialable`}
              className={`h-8 rounded-full text-white text-xs px-4 font-semibold ${
                blockedReason ? "bg-orange-400 hover:bg-orange-500" : "bg-orange-600 hover:bg-orange-700"
              }`}
              data-testid="power-dial-start-btn"
            >
              {powerBusy
                ? <Loader2 size={12} className="animate-spin mr-1.5" />
                : <Zap size={12} className="mr-1.5" strokeWidth={2.4} />}
              Auto-Dial
            </Button>
          );
        })()}
        {!activeBreak && (
          <Button
            size="sm"
            onClick={() => setShowBreakModal(true)}
            className="h-8 rounded-full bg-slate-800 hover:bg-slate-900 text-white text-xs px-4 font-semibold"
            data-testid="dialer-pause-btn"
          >
            <PauseCircle size={12} className="mr-1.5" /> Pause
          </Button>
        )}
        {activeBreak && (
          <Button
            size="sm"
            onClick={async () => {
              setBreakBusy(true);
              try {
                await api.post("/dialer/break/end");
                setActiveBreak(null);
                toast.success("Break ended — ready to dial.");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Couldn't end break");
              } finally { setBreakBusy(false); }
            }}
            disabled={breakBusy}
            className="h-8 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4 font-semibold"
            data-testid="dialer-end-break-btn"
          >
            {breakBusy ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <PlayCircle size={12} className="mr-1.5" />}
            End Break
          </Button>
        )}

        <button
          onClick={() => setListNameFilter("")}
          disabled={!repStats.active_callbacks}
          data-testid="active-callbacks-pill"
          className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest transition ${
            repStats.active_callbacks
              ? "bg-orange-500 text-white hover:bg-orange-600 shadow-md"
              : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
        >
          {repStats.active_callbacks} Active Callback{repStats.active_callbacks === 1 ? "" : "s"}
        </button>
        <div className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-widest ${
          activeBreak ? "bg-amber-100 text-amber-800 border border-amber-300" :
          powerSession ? "bg-emerald-100 text-emerald-800 border border-emerald-300 animate-pulse" :
                          "bg-slate-100 text-slate-500 border border-slate-200"
        }`} data-testid="dialer-live-status">
          {activeBreak ? `On break · ${activeBreak.reason_label || activeBreak.reason}` :
           powerSession ? "LIVE CALL" : "NO LIVE CALL"}
        </div>
        <div className="font-mono text-xs text-slate-600 tabular-nums">
          {wallClock.toLocaleString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}
        </div>
      </div>

      {/* ============================================================ FILTER BAR — compact dropdowns
          Owner ask 2026-08-28 (Dialer_1880): filter strip was too wide;
          collapsed the pill rows into 3 compact dropdowns (List · Board ·
          Column). Same state (`listNameFilter`, `filterBoardId`,
          `filterColumnKey`) is wired underneath so the queue engine
          keeps working unchanged. */}
      <div className="mt-4 card-flat p-3 flex flex-wrap items-center gap-3"
           data-testid="dialer-filter-card">
        {callLists.length > 0 && (
          <label className="flex items-center gap-2 text-[11px]" data-testid="dialer-list-filter">
            <span className="uppercase tracking-widest text-muted-foreground font-semibold">List</span>
            <select
              value={listNameFilter}
              onChange={(e) => {
                const next = e.target.value;
                setListNameFilter(next);
                if (next) { setFilterBoardId(""); setFilterColumnKey(""); }
              }}
              data-testid="dialer-filter-list-select"
              className="h-8 text-[12px] rounded-md border border-[var(--app-line)] bg-white px-2 min-w-[160px] max-w-[260px]"
              title="Filter queue to a single imported CSV list"
            >
              <option value="">All lists</option>
              {callLists.map((l) => (
                <option key={l.name} value={l.name}>
                  {l.name} ({l.with_phone || l.total})
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="flex items-center gap-2 text-[11px]" data-testid="dialer-queue-filter">
          <span className="uppercase tracking-widest text-muted-foreground font-semibold">Board</span>
          <select
            value={filterBoardId}
            onChange={(e) => {
              setFilterBoardId(e.target.value);
              setFilterColumnKey("");
            }}
            data-testid="dialer-filter-board-select"
            className="h-8 text-[12px] rounded-md border border-[var(--app-line)] bg-white px-2 min-w-[180px] max-w-[240px]"
            title="Pull leads from this CRM board only"
          >
            <option value="">All boards</option>
            {boards.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </label>

        {filterBoardId && (
          <label className="flex items-center gap-2 text-[11px]" data-testid="dialer-filter-column-row">
            <span className="uppercase tracking-widest text-muted-foreground font-semibold">Column</span>
            <select
              value={filterColumnKey}
              onChange={(e) => setFilterColumnKey(e.target.value)}
              data-testid="dialer-filter-col-select"
              className="h-8 text-[12px] rounded-md border border-[var(--app-line)] bg-white px-2 min-w-[180px] max-w-[240px]"
              title="Narrow further to a single Pipeline column"
            >
              <option value="">Any column</option>
              {(boards.find((b) => b.id === filterBoardId)?.columns || []).map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </label>
        )}

        {(listNameFilter || filterBoardId || filterColumnKey) && (
          <button
            onClick={() => { setListNameFilter(""); setFilterBoardId(""); setFilterColumnKey(""); }}
            data-testid="dialer-filter-clear"
            className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-red-600 transition ml-auto"
            title="Clear all filters"
          >
            Clear filters
          </button>
        )}
      </div>

      {deviceState === "mic-blocked" && (
        <div className="mt-4 rounded-2xl border border-red-300 bg-red-50 text-red-800 p-4 text-sm"
             data-testid="dialer-mic-help">
          <div className="font-medium mb-1">Microphone is blocked</div>
          <ol className="text-xs list-decimal ml-4 space-y-1 mt-2">
            <li>Click the 🔒 <b>lock icon</b> to the left of the address bar</li>
            <li>Find <b>Microphone</b> and change it to <b>Allow</b></li>
            <li>Reload this page (Cmd/Ctrl + R)</li>
            <li>On macOS also check: System Settings → Privacy &amp; Security → Microphone → enable Chrome</li>
          </ol>
        </div>
      )}

      <div className="mt-8 grid md:grid-cols-[1fr_320px] xl:grid-cols-[1fr_360px] gap-6">
        {/* ================================================ LEFT COLUMN (queue + property / offer) */}
        <div className="space-y-6 min-w-0">

        {/* ============================================================ BATCH TAKEOVER
            When a lead is on the wire we swap the left column to the Batch-style
            top strip + tabs + LEAD INFO form so the rep gets the mockup's exact
            layout. The softphone card stays anchored on the right. */}
        {current && (
          <>
            <DialerBatchHeader
              lead={current}
              callState={callState}
              callStartedAt={callStartedAt}
              activeTab={headerTab}
              onTabChange={setHeaderTab}
              onCallbackSaved={(updated) => {
                setCurrent((c) => ({ ...(c || {}), ...updated }));
                setQueue((q) => q.map((l) => l.id === updated.id ? { ...l, ...updated } : l));
              }}
              dispoCount={0}
              callCount={current?.attempt_count || 0}
              vmCount={0}
            />
            {headerTab === "lead_info" && (
              <DialerLeadInfoPanel
                lead={current}
                onSaved={(updated) => {
                  setCurrent((c) => ({ ...(c || {}), ...updated }));
                  setQueue((q) => q.map((l) => l.id === updated.id ? { ...l, ...updated } : l));
                }}
                slotUnderEmail={
                  <QuickOfferButtons
                    tiers={tiers?.tiers || []}
                    sending={sendingOffer}
                    canSend={!!(propInfo && sellerEmail)}
                    onPreviewTier={(k) => { setSelectedTier(k); setShowOfferPopup(true); }}
                    onSendTier={(k) => { setSelectedTier(k); setTimeout(() => sendOfferNow(), 30); }}
                  />
                }
              />
            )}
            {headerTab !== "lead_info" && (
              <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500"
                   data-testid="dialer-tab-placeholder">
                {headerTab === "property"   && "Property details tab — coming soon."}
                {headerTab === "activities" && "Call & SMS history for this lead — coming soon."}
                {headerTab === "campaigns"  && "Campaigns this lead belongs to — coming soon."}
                {headerTab === "notes"      && "Team notes — use the drawer for now."}
                {headerTab === "sheets"     && "Attached lead sheets — coming soon."}
              </div>
            )}
          </>
        )}

        {/* ============================================================ AUTO-DIAL CONTROL BAR — retired 2026-08-28
            Buttons lifted into the today-bar (Active Callbacks row).
            The whole bar is `hidden` but kept in-tree for the diff. */}
        <div hidden aria-hidden="true"
             className="card-flat p-3 md:p-4 rounded-2xl border border-[var(--app-line)] bg-gradient-to-br from-orange-50/40 to-amber-50/40"
             data-testid="power-dial-bar">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Zap size={14} className="text-orange-600" strokeWidth={2.4} />
              <div className="text-xs font-bold uppercase tracking-widest text-orange-900">
                Auto-Dial Queue · single line
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!powerSession && (() => {
                const dialableCount = queue.filter((l) => String(l.phone || "").replace(/\D/g, "").length >= 10).length;
                const notReady = deviceState !== "ready";
                const notIdle  = callState !== "idle" && callState !== "ended";
                const noLeads  = dialableCount < 1;
                const blockedReason = powerBusy ? "Starting…"
                                    : notReady  ? "Dialer not connected (grant mic access, wait for green)"
                                    : notIdle   ? `Finish the current call first (state: ${callState})`
                                    : noLeads   ? "No dialable leads in the queue (no phone numbers)"
                                    : null;
                const isDisabled = powerBusy;
                return (
                <Button
                  size="sm"
                  onClick={startPowerDial}
                  disabled={isDisabled}
                  title={blockedReason || `Auto-dial the queue — I'll roll into the next lead the moment you save a dispo (${dialableCount} dialable)`}
                  className={`h-9 rounded-full text-white text-xs px-5 font-semibold ${
                    blockedReason
                      ? "bg-orange-400 hover:bg-orange-500"
                      : "bg-orange-600 hover:bg-orange-700"
                  }`}
                  data-testid="power-dial-start-btn"
                >
                  {powerBusy
                    ? <Loader2 size={12} className="animate-spin mr-1.5" />
                    : <Zap size={12} className="mr-1.5" strokeWidth={2.4} />}
                  Start Auto-Dial
                </Button>
                );
              })()}
              {!activeBreak && (
                <Button
                  size="sm"
                  onClick={() => setShowBreakModal(true)}
                  className="h-9 rounded-full bg-slate-800 hover:bg-slate-900 text-white text-xs px-5 font-semibold"
                  data-testid="dialer-pause-btn"
                >
                  <PauseCircle size={12} className="mr-1.5" />
                  Pause
                </Button>
              )}
              {activeBreak && (
                <Button
                  size="sm"
                  onClick={async () => {
                    setBreakBusy(true);
                    try {
                      await api.post("/dialer/break/end");
                      setActiveBreak(null);
                      toast.success("Break ended — ready to dial.");
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Couldn't end break");
                    } finally { setBreakBusy(false); }
                  }}
                  disabled={breakBusy}
                  className="h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-5 font-semibold"
                  data-testid="dialer-end-break-btn"
                >
                  {breakBusy ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <PlayCircle size={12} className="mr-1.5" />}
                  End Break: {activeBreak.reason_label || activeBreak.reason}
                </Button>
              )}
            </div>
            {(transferCanEdit || transferNumber) && (
              <div className="flex items-center gap-2 text-[11px]" data-testid="dialer-transfer-num-row">
                <ArrowRightCircle size={12} className="text-emerald-600" />
                <span className="text-muted-foreground">Acquisitions #:</span>
                {transferCanEdit && transferEditing ? (
                  <>
                    <Input
                      value={transferInput}
                      onChange={(e) => setTransferInput(e.target.value)}
                      placeholder="+1 555 123 4567"
                      className="h-7 text-[11px] w-44"
                      data-testid="dialer-transfer-num-input"
                    />
                    <Button size="sm" onClick={saveTransferNumber}
                            data-testid="dialer-transfer-num-save"
                            className="h-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] px-3">
                      <Save size={11} className="mr-1" />Save
                    </Button>
                    <button onClick={() => { setTransferEditing(false); setTransferInput(transferNumber || ""); }}
                            className="text-muted-foreground hover:text-red-600"
                            data-testid="dialer-transfer-num-cancel">
                      <X size={12} />
                    </button>
                  </>
                ) : (
                  <>
                    <code className="font-mono bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded"
                          data-testid="dialer-transfer-num-value">
                      {transferNumber || "not set"}
                    </code>
                    {transferCanEdit && (
                      <button onClick={() => setTransferEditing(true)}
                              className="text-muted-foreground hover:text-emerald-700"
                              data-testid="dialer-transfer-num-edit"
                              title="Edit acquisitions transfer number">
                        <Pencil size={11} />
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ============================================================ FILTER BAR — RELOCATED
            Owner ask 2026-08-28 (Dialer_2070): filter card moved out of
            the left column and above the grid so it spans full width
            under the Auto-Dial bar. This copy is `hidden` for diff trace. */}
        <div hidden aria-hidden="true"
             className="card-flat p-4 md:p-5"
             data-testid="dialer-filter-card-legacy">
          {/* Call List picker — sourced from /call-lists (CSV imports). */}
          {callLists.length > 0 && (
            <div className="mb-3" data-testid="dialer-list-filter">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                Call from list
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setListNameFilter("")}
                  data-testid="dialer-filter-list-all"
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                    !listNameFilter
                      ? "bg-orange-500 text-white border-orange-500"
                      : "border-[var(--app-line)] hover:border-orange-400/70"
                  }`}
                >
                  All lists
                </button>
                {callLists.slice(0, 12).map((l) => (
                  <button
                    key={l.name}
                    onClick={() => {
                      const next = l.name === listNameFilter ? "" : l.name;
                      setListNameFilter(next);
                      if (next) { setFilterBoardId(""); setFilterColumnKey(""); }
                    }}
                    data-testid={`dialer-filter-list-${l.name.toLowerCase().replace(/\s+/g,'-')}`}
                    title={`${l.total} leads · ${l.with_phone || 0} with phone · ${l.call_progress_pct || 0}% dialed`}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition inline-flex items-center gap-1.5 ${
                      listNameFilter === l.name
                        ? "bg-orange-500 text-white border-orange-500"
                        : "border-[var(--app-line)] hover:border-orange-400/70"
                    }`}
                  >
                    <span className="truncate max-w-[10rem]">{l.name}</span>
                    <span className={`text-[9px] rounded-full px-1.5 py-[1px] ${
                      listNameFilter === l.name
                        ? "bg-white/20 text-white"
                        : "bg-slate-100 text-slate-600"
                    }`}>
                      {l.with_phone || l.total}
                    </span>
                  </button>
                ))}
                {callLists.length > 12 && (
                  <span className="text-[10px] text-slate-500 self-center">
                    +{callLists.length - 12} more · <a href="/call-lists" className="underline">manage</a>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Board / column filter — which CRM column to pull leads from */}
          <div data-testid="dialer-queue-filter">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
              Call leads from
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => { setFilterBoardId(""); setFilterColumnKey(""); }}
                data-testid="dialer-filter-board-all"
                className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                  !filterBoardId
                    ? "bg-[var(--app-accent)] text-white border-[var(--app-accent)]"
                    : "border-[var(--app-line)] hover:border-[var(--app-accent)]/50"
                }`}
              >
                All boards
              </button>
              {boards.map((b) => (
                <button
                  key={b.id}
                  onClick={() => {
                    setFilterBoardId(b.id === filterBoardId ? "" : b.id);
                    setFilterColumnKey("");
                  }}
                  data-testid={`dialer-filter-board-${b.id}`}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                    filterBoardId === b.id
                      ? "bg-[var(--app-accent)] text-white border-[var(--app-accent)]"
                      : "border-[var(--app-line)] hover:border-[var(--app-accent)]/50"
                  }`}
                >
                  {b.name}
                </button>
              ))}
            </div>
            {filterBoardId && (
              <div className="flex flex-wrap gap-1.5 mt-2 pl-3 border-l-2 border-[var(--app-accent)]/30"
                   data-testid="dialer-filter-column-row">
                <button
                  onClick={() => setFilterColumnKey("")}
                  className={`text-[10px] px-2 py-0.5 rounded border transition ${
                    !filterColumnKey
                      ? "bg-slate-800 text-white border-slate-800"
                      : "border-[var(--app-line)] hover:border-slate-500"
                  }`}
                >
                  Any column
                </button>
                {(boards.find((b) => b.id === filterBoardId)?.columns || []).map((c) => (
                  <button
                    key={c.key}
                    onClick={() => setFilterColumnKey(c.key === filterColumnKey ? "" : c.key)}
                    data-testid={`dialer-filter-col-${c.key}`}
                    className={`text-[10px] px-2 py-0.5 rounded border transition ${
                      filterColumnKey === c.key
                        ? "bg-slate-800 text-white border-slate-800"
                        : "border-[var(--app-line)] hover:border-slate-500"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ============================================================ QUEUE
            Card wrapper removed 2026-08-28 — reps wanted less visual chrome
            around the list; rows now flow directly under the filter card. */}
        <div className="rounded-2xl bg-white" data-testid="dialer-queue">
          {/* Filters moved out — see dialer-filter-card above. */}
          {/* Bulk-delete action bar (admin, when in select mode) */}
          {isAdmin && selectMode && (
            <div className="mb-3 rounded-2xl border border-red-200 bg-red-50/60 px-3 py-2 flex items-center justify-between gap-2 flex-wrap"
                 data-testid="dialer-bulk-bar">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-red-900">
                  {selectedIds.size} selected
                </span>
                <button
                  onClick={selectAllInQueue}
                  className="text-[11px] px-2 py-0.5 rounded-full border border-red-200 bg-white hover:bg-red-100 text-red-800"
                  data-testid="dialer-select-all-btn"
                >
                  Select all ({queue.length})
                </button>
                {selectedIds.size > 0 && (
                  <button
                    onClick={clearSelection}
                    className="text-[11px] px-2 py-0.5 rounded-full border border-red-200 bg-white hover:bg-red-100 text-red-800"
                    data-testid="dialer-clear-selection-btn"
                  >
                    Clear
                  </button>
                )}
              </div>
              <Button
                size="sm"
                onClick={bulkDelete}
                disabled={!selectedIds.size || bulkDeleting}
                className="h-8 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs"
                data-testid="dialer-bulk-delete-btn"
              >
                {bulkDeleting
                  ? <Loader2 size={12} className="animate-spin mr-1" />
                  : <Trash2 size={12} className="mr-1" />}
                Delete {selectedIds.size || ""}
              </Button>
            </div>
          )}

          {/* ==================== LEGACY PREDICTIVE DIALER CONTROL BAR — hidden 2026-08-28 ====================
              Bar was lifted above the filters (see the new .power-dial-bar
              near the top of the left column). This copy is hidden but kept
              in-tree so future diffs show the extraction cleanly. */}
          <div hidden aria-hidden="true"
               className="mb-4 rounded-2xl border border-[var(--app-line)] bg-gradient-to-br from-orange-50/40 to-amber-50/40 p-3"
               data-testid="power-dial-bar-legacy">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Zap size={14} className="text-orange-600" strokeWidth={2.4} />
                <div className="text-xs font-bold uppercase tracking-widest text-orange-900">
                  Auto-Dial Queue · single line
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!powerSession && (() => {
                  // Compute what's blocking Speed Dial so we can show the
                  // reason as a tooltip AND disable properly. Previously
                  // this was inlined and reps saw a greyed-out button with
                  // no clue why — turned out to be a stale `callState`
                  // stuck at "ended" after a previous call.
                  const dialableCount = queue.filter((l) => String(l.phone || "").replace(/\D/g, "").length >= 10).length;
                  const notReady = deviceState !== "ready";
                  const notIdle  = callState !== "idle" && callState !== "ended";
                  const noLeads  = dialableCount < 1;
                  const blockedReason = powerBusy ? "Starting…"
                                      : notReady  ? "Dialer not connected (grant mic access, wait for green)"
                                      : notIdle   ? `Finish the current call first (state: ${callState})`
                                      : noLeads   ? "No dialable leads in the queue (no phone numbers)"
                                      : null;
                  const isDisabled = powerBusy;  // let the rep click even if there's a soft-block — startPowerDial toasts a clear reason
                  return (
                  <Button
                    size="sm"
                    onClick={startPowerDial}
                    disabled={isDisabled}
                    title={blockedReason || `Auto-dial the queue — I'll roll into the next lead the moment you save a dispo (${dialableCount} dialable)`}
                    className={`h-9 rounded-full text-white text-xs px-5 font-semibold ${
                      blockedReason
                        ? "bg-orange-400 hover:bg-orange-500"
                        : "bg-orange-600 hover:bg-orange-700"
                    }`}
                    data-testid="power-dial-start-btn"
                  >
                    {powerBusy
                      ? <Loader2 size={12} className="animate-spin mr-1.5" />
                      : <Zap size={12} className="mr-1.5" strokeWidth={2.4} />}
                    Start Auto-Dial
                  </Button>
                  );
                })()}
                {!activeBreak && (
                  <Button
                    size="sm"
                    onClick={() => setShowBreakModal(true)}
                    className="h-9 rounded-full bg-slate-800 hover:bg-slate-900 text-white text-xs px-5 font-semibold"
                    data-testid="dialer-pause-btn"
                  >
                    <PauseCircle size={12} className="mr-1.5" />
                    Pause
                  </Button>
                )}
                {activeBreak && (
                  <Button
                    size="sm"
                    onClick={async () => {
                      setBreakBusy(true);
                      try {
                        await api.post("/dialer/break/end");
                        setActiveBreak(null);
                        toast.success("Break ended — ready to dial.");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Couldn't end break");
                      } finally { setBreakBusy(false); }
                    }}
                    disabled={breakBusy}
                    className="h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-5 font-semibold"
                    data-testid="dialer-end-break-btn"
                  >
                    {breakBusy ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <PlayCircle size={12} className="mr-1.5" />}
                    End Break: {activeBreak.reason_label || activeBreak.reason}
                  </Button>
                )}
              </div>
              {/* Acquisitions Transfer Number — admin-editable inline. Rep sees
                  it read-only so they know the Transfer button is armed. */}
              {(transferCanEdit || transferNumber) && (
                <div className="mt-2 flex items-center gap-2 text-[11px]" data-testid="dialer-transfer-num-row">
                  <ArrowRightCircle size={12} className="text-emerald-600" />
                  <span className="text-muted-foreground">Acquisitions #:</span>
                  {transferCanEdit && transferEditing ? (
                    <>
                      <Input
                        value={transferInput}
                        onChange={(e) => setTransferInput(e.target.value)}
                        placeholder="+1 555 123 4567"
                        className="h-7 text-[11px] w-44"
                        data-testid="dialer-transfer-num-input"
                      />
                      <Button size="sm" onClick={saveTransferNumber}
                              data-testid="dialer-transfer-num-save"
                              className="h-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] px-3">
                        <Save size={11} className="mr-1" />Save
                      </Button>
                      <button onClick={() => { setTransferEditing(false); setTransferInput(transferNumber || ""); }}
                              className="text-muted-foreground hover:text-red-600"
                              data-testid="dialer-transfer-num-cancel">
                        <X size={12} />
                      </button>
                    </>
                  ) : (
                    <>
                      <code className="font-mono bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded"
                            data-testid="dialer-transfer-num-value">
                        {transferNumber || "not set"}
                      </code>
                      {transferCanEdit && (
                        <button onClick={() => setTransferEditing(true)}
                                className="text-muted-foreground hover:text-emerald-700"
                                data-testid="dialer-transfer-num-edit"
                                title="Edit acquisitions transfer number">
                          <Pencil size={11} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            {/* Live legs status — retired 2026-08-21 with the 3-liner. The
                single-line dialer surfaces call state on the main customer
                card, not on a parallel legs grid. `powerSession` is now
                always null except for the transient teardown moment after
                a legacy session reload, so guarding on `false` hides the
                grid without deleting it from the diff for reviewer trace. */}
            {false && powerSession && (
              <div className="mt-3 grid gap-1.5" data-testid="power-dial-legs">
                {(powerSession.legs || []).map((leg, i) => {
                  const isWinner = powerSession.winner_lead_id === leg.lead_id;
                  const vmDropped = (powerSession.voicemail_dropped_for || []).includes(leg.lead_id);
                  const lead = queue.find((l) => l.id === leg.lead_id);
                  let tone = "border-slate-200 bg-white text-slate-600";
                  let statusLabel = leg.state || "dialing";
                  if (isWinner) { tone = "border-emerald-300 bg-emerald-50 text-emerald-800"; statusLabel = "Connected"; }
                  else if (powerSession.winner_lead_id) { tone = "border-slate-200 bg-slate-50 text-slate-500"; statusLabel = "Cancelled"; }
                  else if (vmDropped) { tone = "border-purple-200 bg-purple-50 text-purple-700"; statusLabel = "VM Dropped"; }
                  else if (leg.state === "failed") { tone = "border-red-200 bg-red-50 text-red-700"; statusLabel = "Failed"; }
                  else if (leg.state === "no-phone" || leg.state === "bad-phone") { tone = "border-amber-200 bg-amber-50 text-amber-700"; statusLabel = "Bad #"; }
                  return (
                    <div key={leg.lead_id || i}
                         className={`flex items-center gap-2 text-[11px] px-2 py-1.5 border rounded-lg transition ${tone}`}
                         data-testid={`power-dial-leg-${i}`}>
                      <span className="w-4 h-4 rounded-full bg-white/70 flex items-center justify-center text-[9px] font-bold shrink-0">
                        L{i + 1}
                      </span>
                      <span className="font-medium truncate flex-1">
                        {lead?.name || leg.name || "Unknown"}
                      </span>
                      <span className="font-mono opacity-70 truncate">{leg.phone || "—"}</span>
                      <span className="font-medium uppercase tracking-wider text-[9px] shrink-0">
                        {isWinner && !callState.startsWith("connect") ? "Ringing…" : statusLabel}
                      </span>
                      {isWinner && <PhoneCall size={11} className="shrink-0" />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {queue.length === 0 && !queueBusy && (
            <div className="text-center py-16 text-sm text-muted-foreground">
              <Phone size={40} className="mx-auto mb-3 opacity-30" />
              No leads with phone numbers in your queue.
              <div className="text-[11px] mt-1">Skip trace some leads first, or turn off DNC filters.</div>
            </div>
          )}

          {/* Queue list hidden 2026-08-28 per owner ask (Dialer_2411 tag).
              Reps drive the day through Auto-Dial — the list of names was
              redundant. `queue` state still loads so Auto-Dial and skip
              trace keep working; only the visual rows are suppressed. */}
          <div hidden aria-hidden="true"
               className="space-y-2 max-h-[240px] overflow-y-auto pr-1 -mr-1"
               data-testid="dialer-queue-list"
               style={{ scrollbarWidth: "thin" }}>
            {queue.map((lead, i) => {
              const hasPhone = !!(lead.phone && String(lead.phone).replace(/\D/g, "").length >= 10);
              // ---- Contacted-recently indicator ----
              // Reps kept re-dialling leads they'd already worked earlier in
              // the day because there was no visual cue. Now every lead
              // shows a color badge tied to how recently it was last touched.
              const lastContactMs = lead.last_contact_at
                ? Date.parse(lead.last_contact_at)
                : null;
              const ageMs   = lastContactMs ? (Date.now() - lastContactMs) : null;
              const ageDays = ageMs != null ? ageMs / 86400000 : null;
              const contactTier =
                ageMs == null           ? "never"    :
                ageMs < 4 * 3600 * 1000 ? "just_now" :   // last 4 hours — red
                ageDays < 1             ? "today"    :   // same day — amber
                ageDays < 7             ? "week"     :   // 1-7 days ago — subtle amber
                                          "old";         // >7 days — dim gray
              const contactStyle = {
                never:    "border-[var(--app-line)] hover:border-[var(--app-accent)]/50",
                just_now: "border-red-400 bg-red-50/70 hover:border-red-500",
                today:    "border-amber-400 bg-amber-50/60 hover:border-amber-500",
                week:     "border-amber-200 bg-amber-50/30 hover:border-amber-300",
                old:      "border-slate-300 bg-slate-50/50 hover:border-slate-400",
              }[contactTier];
              const contactBadge = contactTier === "just_now"
                ? { label: "Just called", cls: "bg-red-100 text-red-800 border-red-300" }
                : contactTier === "today"
                ? { label: "Called today", cls: "bg-amber-100 text-amber-800 border-amber-300" }
                : contactTier === "week"
                ? { label: `Called ${Math.round(ageDays)}d ago`, cls: "bg-amber-50 text-amber-700 border-amber-200" }
                : contactTier === "old"
                ? { label: `Last ${new Date(lastContactMs).toLocaleDateString(undefined, {month:"numeric", day:"numeric"})}`, cls: "bg-slate-100 text-slate-600 border-slate-300" }
                : null;
              return (
              <div
                key={lead.id}
                data-testid={`dialer-queue-item-${i}`}
                data-contact-tier={contactTier}
                className={`flex items-center gap-3 p-3 border rounded-2xl transition ${
                  current?.id === lead.id
                    ? "border-[var(--app-accent)] bg-[var(--app-accent)]/5"
                    : selectedIds.has(lead.id)
                    ? "border-red-300 bg-red-50/40"
                    : contactStyle
                }`}
                title={contactBadge ? `${contactBadge.label}${lead.last_disposition ? ` — ${lead.last_disposition}` : ""}` : undefined}
              >
                {isAdmin && selectMode && (
                  <input
                    type="checkbox"
                    checked={selectedIds.has(lead.id)}
                    onChange={() => toggleSelected(lead.id)}
                    className="w-4 h-4 accent-red-600 shrink-0 cursor-pointer"
                    data-testid={`dialer-select-lead-${i}`}
                    aria-label={`Select ${lead.name || lead.phone}`}
                  />
                )}
                <div className="w-8 h-8 rounded-full bg-[var(--app-surface)] flex items-center justify-center shrink-0">
                  <User size={14} className="text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{lead.name || "Unknown"}</div>
                  <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1.5">
                    <span className={`font-mono ${!hasPhone ? "italic opacity-60" : ""}`}>
                      {hasPhone ? lead.phone : "no phone on file"}
                    </span>
                    {lead.property_address && <><span>·</span><MapPin size={10} /> {lead.property_address}</>}
                  </div>
                </div>
                {lead.motivation_score != null && (
                  <div className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[var(--app-accent)]/10 text-[var(--app-accent-dark)] shrink-0">
                    M{lead.motivation_score}
                  </div>
                )}
                {contactBadge && (
                  <div
                    className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full border shrink-0 ${contactBadge.cls}`}
                    data-testid={`dialer-contact-badge-${i}`}
                    title={lead.last_disposition ? `Last disposition: ${lead.last_disposition}` : contactBadge.label}
                  >
                    {contactBadge.label}
                  </div>
                )}
                {lead.phone_count > 1 && (
                  <div
                    className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200 shrink-0 inline-flex items-center gap-1"
                    data-testid={`dialer-phone-count-${i}`}
                    title={`${lead.phone_count} numbers on file — Wrong # will rotate through them`}
                  >
                    <PhoneCall size={9} strokeWidth={2.4} />
                    {lead.phone_count}
                  </div>
                )}
                {hasPhone ? (
                  <Button
                    size="sm"
                    // Only disable this Call button if the device is truly
                    // not ready OR we're already talking to THIS same lead.
                    // Otherwise a new lead can always be dialed even if the
                    // last call didn't reach "idle" state (e.g. rep closed
                    // the dispo dialog without saving).
                    disabled={deviceState !== "ready" || (callState !== "idle" && current?.id === lead.id)}
                    onClick={() => dialLead(lead)}
                    className={`rounded-full h-8 shrink-0 relative transition-all ${
                      // Light-on-dial (owner ask 2026-08-28):
                      //   yellow pulse ring while dialing this lead,
                      //   green solid ring the moment audio is connected.
                      current?.id === lead.id && (callState === "ringing" || callState === "connecting")
                        ? "ring-4 ring-amber-400/70 shadow-[0_0_14px_rgba(251,191,36,0.85)] bg-amber-500 hover:bg-amber-500 animate-pulse"
                        : current?.id === lead.id && callState === "connected"
                        ? "ring-4 ring-emerald-400/80 shadow-[0_0_14px_rgba(52,211,153,0.9)] bg-emerald-600 hover:bg-emerald-600"
                        : ""
                    }`}
                    data-testid={`dialer-call-lead-${i}`}
                    data-call-state={
                      current?.id === lead.id ? callState : "idle"
                    }
                  >
                    <PhoneCall size={12} className="mr-1" />
                    {current?.id === lead.id && callState === "connected" ? "Live" :
                     current?.id === lead.id && (callState === "ringing" || callState === "connecting") ? "Dialing" :
                     "Call"}
                  </Button>
                ) : (
                  <Button
                    size="sm" variant="outline"
                    onClick={async () => {
                      try {
                        const { data } = await api.post("/skip-trace/apify-run", { lead_id: lead.id });
                        if (data.phones && data.phones.length > 0) {
                          toast.success(`Found phone: ${data.phones[0]}`);
                          loadQueue();
                        } else {
                          toast.info("No phone found");
                        }
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Skip trace failed");
                      }
                    }}
                    className="rounded-full h-8 text-[11px] shrink-0 border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid={`dialer-skiptrace-lead-${i}`}
                  >
                    <Sparkles size={10} className="mr-1" /> Skip trace
                  </Button>
                )}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const name = lead.name || lead.phone || "this lead";
                      if (!window.confirm(`Delete "${name}" permanently? This cannot be undone.`)) return;
                      try {
                        await api.delete(`/leads/${lead.id}`);
                        toast.success(`Deleted ${name}`);
                        setQueue((q) => q.filter((l) => l.id !== lead.id));
                        if (current?.id === lead.id) {
                          setCurrent(null);
                          setCallState("idle");
                        }
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Delete failed");
                      }
                    }}
                    className="p-1.5 rounded-full text-muted-foreground hover:text-red-600 hover:bg-red-50 transition shrink-0"
                    title="Delete lead (admin only)"
                    data-testid={`dialer-delete-lead-${i}`}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            );})}
          </div>
          {false && queue.length > 3 && (
            <div className="mt-2 text-center text-[10px] text-muted-foreground"
                 data-testid="dialer-queue-hint">
              Showing 3 · scroll for {queue.length - 3} more
            </div>
          )}
        </div>

        {/* ---- MOVED: Live property + offer panel — sits directly under the queue in the LEFT column
             so reps see the numbers without scrolling past the dispo/softphone column. ---- */}

      {/* ============================================================ LIVE PROPERTY PANEL — hidden 2026-08-28
          Owner ask (Dialer_2606): removed — property facts already live
          inside the Batch header + LeadInfoPanel. Kept in-tree behind
          `hidden` for the diff trace. */}
      {false && current && !current.id?.startsWith("manual-") && (
        <div className="card-flat p-5 md:p-6" data-testid="dialer-live-property">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[var(--app-accent)]/10 flex items-center justify-center">
                <HomeIcon size={14} className="text-[var(--app-accent-dark)]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Live property analysis
                </div>
                <div className="font-medium text-sm">
                  {propInfo?.address || current.property_address || "Loading…"}
                </div>
              </div>
            </div>
            {propInfo?.motivation_score != null && (
              <div className="text-[10px] font-mono px-2 py-1 rounded bg-red-100 text-red-700 border border-red-200">
                Motivation {propInfo.motivation_score}
              </div>
            )}
          </div>

          {propLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <Loader2 size={20} className="animate-spin mx-auto mb-2" />
              Pulling property info…
            </div>
          ) : propInfo ? (
            <div className="grid md:grid-cols-[1fr_1fr_1.4fr] gap-5">
              {/* --- Property facts --- */}
              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  The house
                </div>
                <PropRow label="Owner"   value={propInfo.owner} />
                <PropRow label="Sqft"    value={propInfo.sqft && propInfo.sqft.toLocaleString()} />
                <PropRow label="Beds / Baths"
                         value={(propInfo.beds != null || propInfo.baths != null)
                                ? `${propInfo.beds ?? "–"} / ${propInfo.baths ?? "–"}` : null} />
                <PropRow label="Year built" value={propInfo.year_built} />
                <PropRow label="City"    value={[propInfo.city, propInfo.state].filter(Boolean).join(", ")} />
              </div>

              {/* --- Numbers --- */}
              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  The numbers
                </div>
                <PropRow icon={TrendingUp}
                         label="ARV"
                         value={propInfo.arv && `$${Math.round(propInfo.arv).toLocaleString()}`} />
                <PropRow icon={Wrench}
                         label="Repair est."
                         value={propInfo.repairs_estimate && `$${Math.round(propInfo.repairs_estimate).toLocaleString()}`} />
                <PropRow label="Market value"
                         value={propInfo.market_value && `$${Math.round(propInfo.market_value).toLocaleString()}`} />
                <PropRow label="Assessed"
                         value={propInfo.assessed_value && `$${Math.round(propInfo.assessed_value).toLocaleString()}`} />
              </div>

              {/* --- Make offer NOW --- */}
              <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 p-4">
                <div className="text-[10px] uppercase tracking-widest text-emerald-800 flex items-center gap-1">
                  <Sparkles size={10} /> Smart offer tiers
                  {tiersLoading && (
                    <Loader2 size={10} className="animate-spin ml-1 opacity-70" />
                  )}
                </div>

                {/* Owner / Asking Price — overrides ARV as the % base when set */}
                <div className="mt-2">
                  <Label className="text-[10px] text-emerald-900 flex items-center gap-1">
                    <DollarSign size={10} /> Owner / Asking Price
                    <span className="text-[9px] text-emerald-800/70 font-normal">
                      (optional — recalculates off this instead of ARV)
                    </span>
                  </Label>
                  <Input
                    type="number"
                    value={ownerPrice}
                    onChange={(e) => setOwnerPrice(e.target.value)}
                    className="mt-1 h-8 text-sm font-mono bg-white"
                    data-testid="dialer-owner-price-input"
                    placeholder={propInfo.arv ? `e.g. $${Math.round(propInfo.arv * 0.9).toLocaleString()}` : "e.g. 220000"}
                  />
                  {Number(ownerPrice) > 0 && (
                    <div className="text-[9px] text-emerald-800/80 mt-1"
                         data-testid="dialer-owner-price-badge">
                      ✓ Tiers now use Owner Price · ARV ${propInfo.arv?.toLocaleString()} kept for reference
                    </div>
                  )}
                </div>

                {/* Verbal-cue repair chips — one tap to reset repairs from the call */}
                <div className="mt-3">
                  <Label className="text-[10px] text-emerald-900 flex items-center gap-1">
                    <Wrench size={10} /> Repair condition
                    <span className="text-[9px] text-emerald-800/70 font-normal">
                      (from what the seller just said)
                    </span>
                  </Label>
                  <div className="grid grid-cols-4 gap-1 mt-1"
                       data-testid="dialer-condition-chips">
                    {[
                      { key: "cosmetic", label: "Cosmetic", rate: "$12", hint: "Paint / carpet — dated but livable" },
                      { key: "medium",   label: "Medium",   rate: "$28", hint: "Kitchen / bath refresh + minor systems" },
                      { key: "heavy",    label: "Heavy",    rate: "$55", hint: "Vacant, water damage, big systems" },
                      { key: "teardown", label: "Teardown", rate: "$100", hint: "Fire / foundation / condemned" },
                    ].map((c) => {
                      const isActive = conditionKey === c.key;
                      const tones = {
                        cosmetic: isActive ? "bg-emerald-600 text-white border-transparent" :
                                             "bg-white text-emerald-900 border-emerald-300 hover:bg-emerald-50",
                        medium:   isActive ? "bg-sky-600 text-white border-transparent" :
                                             "bg-white text-sky-900 border-sky-300 hover:bg-sky-50",
                        heavy:    isActive ? "bg-orange-600 text-white border-transparent" :
                                             "bg-white text-orange-900 border-orange-300 hover:bg-orange-50",
                        teardown: isActive ? "bg-rose-700 text-white border-transparent" :
                                             "bg-white text-rose-900 border-rose-300 hover:bg-rose-50",
                      };
                      return (
                        <button
                          key={c.key}
                          type="button"
                          onClick={() => setConditionKey(c.key)}
                          title={c.hint}
                          data-testid={`dialer-condition-${c.key}`}
                          className={`rounded-lg border py-1.5 text-center transition-colors ${tones[c.key]}`}
                        >
                          <div className="text-[9px] font-semibold uppercase tracking-wider leading-tight">
                            {c.label}
                          </div>
                          <div className={`text-[8px] mt-0.5 ${isActive ? "opacity-90" : "opacity-60"}`}>
                            {c.rate}/sqft
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {tiers?.repairs != null && Number(propInfo.sqft) > 0 && (
                    <div className="text-[9px] text-emerald-800/70 mt-1"
                         data-testid="dialer-condition-repair-total">
                      Repair estimate: <span className="font-mono font-semibold">
                        ${Math.round(tiers.repairs).toLocaleString()}
                      </span>{" "}
                      ({Math.round(propInfo.sqft).toLocaleString()} sqft × ${tiers.repair_cost_per_sqft}/sqft)
                    </div>
                  )}
                  {tiers?.repairs != null && !(Number(propInfo.sqft) > 0) && (
                    <div className="text-[9px] text-amber-800/80 mt-1"
                         data-testid="dialer-condition-repair-lumpsum">
                      Repair estimate: <span className="font-mono font-semibold">
                        ${Math.round(tiers.repairs).toLocaleString()}
                      </span>{" "}
                      (sqft not on file — using {conditionKey} lump-sum)
                    </div>
                  )}
                </div>

                {/* 3-tier picker — click any tier to load it into the offer input */}
                {/* key on the outer grid forces a full remount every time the
                    condition chip changes → belt-and-braces against any React
                    reconciliation caching stale tier prices. */}
                {tiers?.tiers?.length ? (
                  <div className="grid grid-cols-3 gap-1.5 mt-2"
                       key={`${conditionKey}-${Math.round(tiers?.repairs || 0)}`}
                       data-testid="dialer-smart-tiers">
                    {tiers.tiers.map((t) => {
                      const isActive = selectedTier === t.key;
                      const palette = t.key === "quick"
                        ? { chip: "bg-emerald-600 text-white",
                            idle: "bg-white text-emerald-900 border-emerald-300 hover:bg-emerald-50" }
                        : t.key === "max_safe"
                        ? { chip: "bg-amber-600 text-white",
                            idle: "bg-white text-amber-900 border-amber-300 hover:bg-amber-50" }
                        : { chip: "bg-violet-600 text-white",
                            idle: "bg-white text-violet-900 border-violet-300 hover:bg-violet-50" };
                      return (
                        <button
                          key={`${t.key}-${Math.round(t.price)}`}
                          data-testid={`dialer-tier-${t.key}`}
                          onClick={() => {
                            setSelectedTier(t.key);
                            setOfferPrice(String(Math.round(t.price)));
                          }}
                          className={`rounded-xl border p-2 text-left transition-colors ${
                            isActive ? palette.chip + " border-transparent" : palette.idle
                          }`}
                          title={t.headline}
                        >
                          <div className="text-[8px] uppercase tracking-widest opacity-80">
                            {t.key === "quick" ? "Quick" :
                             t.key === "max_safe" ? "Max Safe" : "Recommended"}
                          </div>
                          <div className="font-mono font-bold text-sm mt-0.5 leading-tight">
                            ${Math.round(t.price).toLocaleString()}
                          </div>
                          <div className={`text-[8px] mt-0.5 ${isActive ? "opacity-90" : "opacity-60"}`}>
                            {t.arv_pct}% {tiers?.base_source === "owner_price" ? "Owner" : "ARV"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <div className="text-3xl font-bold text-emerald-900 mt-1"
                         data-testid="dialer-suggested-offer">
                      {propInfo.suggested_offer
                        ? `$${Math.round(propInfo.suggested_offer).toLocaleString()}`
                        : "—"}
                    </div>
                    <div className="text-[10px] text-emerald-700/80 mt-0.5">
                      70% of ARV minus repairs
                    </div>
                  </>
                )}

                {/* Risk flags from the smart recommender */}
                {tiers?.flags?.length > 0 && (
                  <div className="mt-2 text-[10px] text-rose-800 bg-rose-50 border border-rose-200 rounded-lg p-2 leading-snug"
                       data-testid="dialer-tier-flags">
                    {tiers.flags.includes("short-sale-risk") && "⚠ Mortgage exceeds Max Safe — short-sale territory."}
                    {!tiers.flags.includes("short-sale-risk") &&
                     tiers.flags.includes("mortgage-above-recommended") && "⚠ Mortgage above Recommended — negotiate at Max Safe."}
                    {!tiers.flags.includes("short-sale-risk") &&
                     !tiers.flags.includes("mortgage-above-recommended") &&
                     tiers.flags.includes("low-equity") && "⚠ Low equity — expect resistance."}
                    {!tiers.flags.includes("short-sale-risk") &&
                     !tiers.flags.includes("mortgage-above-recommended") &&
                     !tiers.flags.includes("low-equity") &&
                     tiers.flags.includes("heavy-rehab") && "⚠ Heavy rehab — verify repair estimate."}
                    {tiers.flags.includes("asking-above-market") && (
                      <div className="mt-1">⚠ Asking price is above 85% of ARV — anchor them back toward ARV.</div>
                    )}
                  </div>
                )}

                <div className="mt-3 space-y-2">
                  <div>
                    <Label className="text-[10px]">Offer amount</Label>
                    <Input type="number" value={offerPrice}
                           onChange={(e) => setOfferPrice(e.target.value)}
                           className="mt-1 h-9 text-sm font-mono bg-white"
                           data-testid="dialer-offer-price-input"
                           placeholder="250000" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Seller email (for signature)</Label>
                    {knownEmails.length > 0 && emailMode === "select" ? (
                      <select
                        value={sellerEmail}
                        onChange={(e) => {
                          if (e.target.value === "__new__") {
                            setEmailMode("new");
                            setSellerEmail("");
                          } else {
                            setSellerEmail(e.target.value);
                          }
                        }}
                        className="mt-1 h-9 w-full rounded-md border border-input bg-white px-2 text-xs"
                        data-testid="dialer-seller-email-select"
                      >
                        {knownEmails.map((em) => (
                          <option key={em} value={em}>{em}</option>
                        ))}
                        <option value="__new__">+ Add new email…</option>
                      </select>
                    ) : (
                      <div className="mt-1 flex gap-1.5">
                        <Input
                          type="email"
                          value={sellerEmail}
                          onChange={(e) => setSellerEmail(e.target.value)}
                          className="h-9 text-xs bg-white flex-1"
                          data-testid="dialer-seller-email-input"
                          placeholder="seller@example.com"
                          autoFocus={emailMode === "new" && knownEmails.length > 0}
                        />
                        {knownEmails.length > 0 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 px-2 text-[10px]"
                            data-testid="dialer-seller-email-back-btn"
                            onClick={() => {
                              setEmailMode("select");
                              setSellerEmail(knownEmails[0] || "");
                            }}
                            title="Pick from saved emails"
                          >
                            <X size={12} />
                          </Button>
                        )}
                      </div>
                    )}
                    {sellerEmail && user?.email &&
                     sellerEmail.toLowerCase() === user.email.toLowerCase() && (
                      <div className="text-[10px] text-red-600 mt-1 flex items-start gap-1"
                           data-testid="dialer-seller-email-warning">
                        <span>⚠</span>
                        <span>Heads up — this is your OWN login email, so the seller won&apos;t receive it. Update it to their real address.</span>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={sendOfferNow}
                    disabled={sendingOffer || !offerPrice || !sellerEmail}
                    data-testid="dialer-send-offer-btn"
                    className="w-full rounded-full h-10 font-medium text-white bg-emerald-600 hover:bg-emerald-700"
                  >
                    {sendingOffer ? (
                      <><Loader2 size={14} className="animate-spin mr-1.5" /> Sending…</>
                    ) : (
                      <><FileSignature size={14} className="mr-1.5" />
                        Send offer for signature
                      </>
                    )}
                  </Button>
                </div>
                <div className="text-[10px] text-emerald-800/70 mt-2 text-center">
                  Creates offer, generates PDF, and emails the seller via SignWell — mid-call
                </div>
              </div>
            </div>
          ) : (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No property info yet — run a Property Lookup for this address first
              or add ARV manually via the lead detail drawer.
            </div>
          )}
        </div>
      )}

        </div>  {/* /left column wrapper (queue + property) */}

        {/* ============================================================ SOFTPHONE */}
        <div className="space-y-4 self-start">
          <div className="card-flat p-5 sticky top-4" data-testid="dialer-softphone">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Softphone</div>
              {/* Call-state lights (owner ask 2026-08-28): yellow = dialing,
                  green = connected. Two dots always visible so the rep
                  can see at a glance what the softphone is doing without
                  reading text. */}
              <div className="flex items-center gap-1.5" data-testid="dialer-call-lights">
                <span
                  className={`inline-block w-2.5 h-2.5 rounded-full transition ${
                    callState === "ringing" || callState === "connecting"
                      ? "bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.9)] animate-pulse"
                      : "bg-amber-200/40"
                  }`}
                  title="Dialing"
                  data-testid="dialer-light-dialing"
                />
                <span
                  className={`inline-block w-2.5 h-2.5 rounded-full transition ${
                    callState === "connected"
                      ? "bg-emerald-500 shadow-[0_0_10px_rgba(52,211,153,0.9)]"
                      : "bg-emerald-200/40"
                  }`}
                  title="Connected"
                  data-testid="dialer-light-connected"
                />
              </div>
            </div>

            {/* SOMETHING STUCK? FORCE END EVERYTHING — always-visible
                escape hatch (owner ask 2026-08-28, arrow "Move this here").
                Renders inside the softphone card body regardless of call
                state so reps can bail whenever the WebRTC/SIP session
                goes wonky. */}
            <button
              type="button"
              onClick={forceEndAllCalls}
              data-testid="dialer-force-end-all-header"
              className="w-full mb-3 rounded-lg border-2 border-dashed border-red-200 hover:border-red-400 bg-red-50/40 hover:bg-red-50 text-red-800 transition py-2 px-3 flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-widest"
              title="Force-end every active call across every browser tab"
            >
              <PhoneOff size={11} />
              Something stuck? Force end everything
            </button>

            {/* DTMF number keypad (owner ask 2026-08-28). Rep taps digits
                mid-call to navigate IVRs (press 1 for sales, etc.). Only
                enabled while a call is live so accidental taps don't
                dial nothing. */}
            <div className="mb-3" data-testid="dialer-keypad-block">
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5 text-center">
                Keypad
              </div>
              <div className="grid grid-cols-3 gap-1.5" data-testid="dialer-keypad">
                {["1","2","3","4","5","6","7","8","9","*","0","#"].map((d) => (
                  <button
                    key={d}
                    type="button"
                    disabled={callState !== "connected"}
                    onClick={() => { try { callRef.current?.sendDigits?.(d); } catch (_) { /* ignore */ } }}
                    data-testid={`dialer-keypad-${d}`}
                    className={`h-9 rounded-lg font-mono text-sm font-bold transition ${
                      callState === "connected"
                        ? "bg-slate-100 hover:bg-slate-200 text-slate-800"
                        : "bg-slate-50 text-slate-300 cursor-not-allowed"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>

            {/* Empty state — whenever there's no active lead. Previously this
                was gated on callState === "idle" too, which meant after the
                last call in a queue the softphone card went BLANK (state
                stuck at "ended", current null) with no way for the rep to
                start a fresh dial without a page refresh. */}
            {!current && (
              <>
                {/* Ghost-call bail-out. Shows when the underlying Twilio Device
                    still holds an active connection while our React state
                    thinks we're idle — happens if a Cancel All half-cleaned
                    up. Gives the rep an escape hatch that also cancels any
                    orphaned server-side power-dial session. */}
                {ghostCall && (
                  <div className="mb-3 rounded-lg border border-red-300 bg-red-50 p-3 space-y-2"
                       data-testid="dialer-ghost-call-banner">
                    <div className="flex items-start gap-2">
                      <PhoneOff size={16} className="text-red-600 mt-0.5 shrink-0" />
                      <div className="text-xs text-red-900 leading-snug">
                        <strong>Live call detected</strong> — the softphone shows
                        idle but the browser is still on a call. Click below to
                        force-end everything.
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={forceEndAllCalls}
                      data-testid="dialer-force-end-all-btn"
                      className="w-full rounded-full bg-red-600 hover:bg-red-700 text-white text-xs"
                    >
                      <PhoneOff size={12} className="mr-1" /> Force End All Calls
                    </Button>
                  </div>
                )}
                <div className="py-4 text-center text-sm text-muted-foreground">
                  <Phone size={30} className="mx-auto mb-2 opacity-30" />
                  Pick a lead from the queue to start
                </div>
                {/* Manual dial input removed 2026-08-28 per owner ask:
                    reps should never be able to dial ad-hoc phone numbers
                    that aren't tied to a CRM lead. Every call needs a
                    lead record so dispositions, warm transfers, and
                    campaign attribution stay clean. Force-end link is
                    now anchored to the softphone header (always visible),
                    so we don't repeat it here. */}
              </>
            )}

            {current && (
              <div>
                {/* Batch header + Lead Info panel now render at page top
                    (full-width) — see BATCH TAKEOVER block above. Here we
                    only render the compact softphone controls. */}
                <div className="text-xs text-muted-foreground" data-testid="dialer-state-label">
                  {callState === "connected" ? "Connected"
                    : callState === "ringing"    ? "Ringing"
                    : callState === "connecting" ? "Connecting"
                    : callState === "ended"      ? "Call ended"
                    : callState === "idle"       ? "Call ended"
                    : "Calling"}
                </div>
                <div className="font-medium text-lg mt-0.5" data-testid="dialer-current-name">{current.name || "Unknown"}</div>
                {/* Phone line hidden 2026-08-28 per owner ask — reps
                    shouldn't be tempted to memo / redial raw numbers.
                    Address stays. */}
                {current.property_address && (
                  <div className="text-[11px] text-muted-foreground mt-1 flex items-start gap-1">
                    <MapPin size={10} className="mt-0.5 shrink-0" />
                    <span>{current.property_address}</span>
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between gap-2">
                  <StateBadge state={callState} startedAt={callStartedAt} />
                  {/* End Call button visible during ringing / connecting too —
                      lets the rep bail on wrong-number recordings ("this number
                      is no longer in service...") without waiting for them to
                      finish. Also shown when connected as a quick shortcut. */}
                  {(callState === "ringing" || callState === "connecting" || callState === "connected") && (
                    <Button
                      size="sm"
                      onClick={hangUp}
                      data-testid="dialer-end-call-btn"
                      className="rounded-full h-8 bg-red-600 hover:bg-red-700 text-white text-xs px-3"
                      title="End call — skips voice prompts so you can dispose immediately"
                    >
                      <PhoneOff size={12} className="mr-1" /> End call
                    </Button>
                  )}
                </div>

                {callState === "connected" && (
                  <>
                    <div className="mt-4 grid grid-cols-3 gap-2" data-testid="dialer-controls">
                      <Button size="sm" variant="outline" onClick={toggleMute}
                              data-testid="dialer-mute-btn"
                              className={`rounded-full ${muted ? "bg-red-50 border-red-300 text-red-700" : ""}`}>
                        {muted ? <MicOff size={14} /> : <Mic size={14} />}
                      </Button>
                      <Button size="sm" variant="outline"
                              onClick={() => { const d = prompt("DTMF digits (e.g. 1):"); if (d) dtmf(d); }}
                              data-testid="dialer-dtmf-btn"
                              className="rounded-full">
                        <Hash size={14} />
                      </Button>
                      <Button size="sm" onClick={hangUp} data-testid="dialer-hangup-btn"
                              className="rounded-full bg-red-600 hover:bg-red-700 text-white col-span-1">
                        <PhoneOff size={14} />
                      </Button>
                    </div>
                    {voicemails.length > 0 && (
                      <div className="mt-2" data-testid="dialer-vm-drop-row">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                          Hit voicemail? Drop one
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {voicemails.map((vm) => (
                            <Button key={vm.id} size="sm" variant="outline"
                                    onClick={() => dropVoicemail(vm.id)}
                                    disabled={droppingVm}
                                    data-testid={`dialer-drop-vm-${vm.id}`}
                                    className="rounded-full h-7 text-[11px] border-purple-300 text-purple-700 hover:bg-purple-50">
                              {droppingVm ? <Loader2 size={10} className="animate-spin mr-1" /> : <Voicemail size={10} className="mr-1" />}
                              {vm.name}{vm.is_default && " ★"}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
                {callState === "connecting" && (
                  <Button size="sm" onClick={hangUp} data-testid="dialer-cancel-btn"
                          className="mt-4 rounded-full bg-red-600 hover:bg-red-700 text-white w-full">
                    <PhoneOff size={14} className="mr-1" /> Cancel
                  </Button>
                )}
              </div>
            )}

            {/* ==================================================== DISPOSITION
                "HOW DID IT GO" modal — pops in the middle of the screen
                whenever a call ends (single-line OR Speed Dialer). The rep
                MUST pick an outcome so nothing gets lost silently. Was
                previously an inline panel tucked into the right-side card
                which reps missed on Speed Dialer.
            */}
            {showDispo && current && (
            <Dialog open={showDispo} onOpenChange={(o) => { if (!o) setShowDispo(false); }}>
              <DialogContent className="max-w-lg" data-testid="dialer-disposition-modal">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 font-display">
                    <PhoneOff size={18} className="text-red-600" />
                    How did it go?
                    <AutoDisposeCountdown
                      show={showDispo}
                      seconds={dialerPrefsRef.current.auto_dispose_delay_sec || 0}
                      onFire={() => {
                        const key = dialerPrefsRef.current.auto_dispose_key || "no-answer";
                        // eslint-disable-next-line no-console
                        console.info(`[AUTO-DISPOSE] firing ${key} — rep didn't pick a chip`);
                        saveDisposition(key);
                      }}
                    />
                  </DialogTitle>
                  <DialogDescription>
                    Call ended with <b>{current?.name || current?.phone || "—"}</b>
                    {current?.property_address ? <> · <span className="font-mono text-[11px]">{current.property_address}</span></> : null}
                  </DialogDescription>
                </DialogHeader>
                <div data-testid="dialer-disposition">
                  <div className="grid grid-cols-2 gap-1.5">
                    {DISPOSITIONS.map((d) => (
                      <button
                        key={d.key}
                        onClick={() => saveDisposition(d.key)}
                        disabled={savingDispo}
                        data-testid={`dialer-dispo-${d.key}`}
                        className={`text-[11px] py-2 px-2 rounded-lg border flex items-center gap-1.5 transition hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${d.tone}`}
                      >
                        <d.icon size={11} /> {d.label}
                      </button>
                    ))}
                  </div>
                  {dispoChoice === "callback" && (
                    <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="dialer-callback-picker">
                      <Label className="text-[11px] text-blue-800">When should we call them back?</Label>
                      <Input
                        type="datetime-local"
                        value={callbackAt}
                        onChange={(e) => setCallbackAt(e.target.value)}
                        className="mt-1 h-9 text-xs"
                        data-testid="dialer-callback-datetime"
                      />
                      <Button size="sm" onClick={() => saveDisposition("callback")}
                              disabled={!callbackAt}
                              data-testid="dialer-callback-confirm"
                              className="mt-2 w-full rounded-full h-8 text-xs">
                        Save callback
                      </Button>
                    </div>
                  )}
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground mt-3 block">
                    Note (optional)
                  </Label>
                  <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                            className="mt-1 text-xs" placeholder="What did they say?"
                            data-testid="dialer-note-input" />

                  {/* Tier Nudge SMS opt-in — visible only when the agent viewed a
                      tier but didn't send an offer during the call */}
                  {!offerSentThisCall &&
                   tiers?.tiers?.find((t) => t.key === selectedTier)?.price &&
                   current?.phone && (
                    <label
                      className="mt-3 flex items-start gap-2 rounded-lg bg-violet-50 border border-violet-200 p-2.5 text-[11px] cursor-pointer"
                      data-testid="dialer-tier-nudge-optin"
                    >
                      <input
                        type="checkbox"
                        checked={tierNudgeOptIn}
                        onChange={(e) => setTierNudgeOptIn(e.target.checked)}
                        className="mt-0.5 accent-violet-600"
                        data-testid="dialer-tier-nudge-checkbox"
                      />
                      <div className="flex-1 text-violet-900 leading-snug">
                        <div className="font-semibold">Send follow-up SMS?</div>
                        <div className="opacity-80">
                          On <b>voicemail / no-answer / callback</b>, drop a text
                          quoting your{" "}
                          <b>${Math.round(
                            tiers.tiers.find((t) => t.key === selectedTier).price
                          ).toLocaleString()}</b>
                          {" "}
                          {selectedTier === "quick" ? "Quick" :
                           selectedTier === "max_safe" ? "Max Safe" : "Recommended"}
                          {" "}offer so the lead stays warm.
                        </div>
                      </div>
                    </label>
                  )}
                </div>
                <DialogFooter>
                  <Button size="sm" variant="ghost" onClick={skipLead}
                          data-testid="dialer-skip-btn"
                          className="text-xs text-muted-foreground">
                    Skip without saving <ChevronRight size={12} className="ml-1" />
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            )}
          </div>

          {/* Script panel — visible during a call so the agent can read as they talk */}
          {scripts.length > 0 && (
            <div className="card-flat p-4" data-testid="dialer-script-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                  <BookText size={10} /> Talk track
                </div>
                {scripts.length > 1 && (
                  <select
                    value={activeScript?.id || ""}
                    onChange={(e) => setActiveScript(scripts.find((s) => s.id === e.target.value) || null)}
                    className="text-[11px] rounded-md border border-[var(--app-line)] px-1.5 py-0.5 bg-white"
                    data-testid="dialer-script-select"
                  >
                    {scripts.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}{s.is_default ? " ★" : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {activeScript ? (
                <div className="text-xs whitespace-pre-wrap leading-relaxed"
                     data-testid="dialer-script-body">
                  {renderedScript(activeScript.body)}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground italic">No script selected</div>
              )}
              <div className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
                <Sparkles size={9} />
                Placeholders: {"{name} {address} {phone} {city} {state}"}
              </div>
            </div>
          )}

          {/* stats card removed per admin 2026-02-17 — pool numbers are visible
              elsewhere in the flow via the caller-ID rotation status. */}
        </div>
      </div>

      {/* ==================================================== MANAGE DIALOG */}
      <ManageDialog
        kind={manageOpen}
        onClose={() => setManageOpen(null)}
        scripts={scripts}
        voicemails={voicemails}
        onScriptsChanged={loadScripts}
        onVoicemailsChanged={loadVoicemails}
      />

      {/* ==================== BREAK-CODE MODAL ==================== */}
      {/* Blocks dial-close: rep MUST pick a reason for pausing. Kicks off */}
      {/* a server-side break record that feeds shift-report analytics. */}
      <Dialog open={showBreakModal} onOpenChange={setShowBreakModal}>
        <DialogContent className="max-w-md" data-testid="break-modal">
          <DialogHeader>
            <DialogTitle>Why are you pausing?</DialogTitle>
            <DialogDescription>
              The dialer will stop auto-firing until you resume. Your break
              time is logged in shift reports.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {[
              { key: "bathroom", label: "Bathroom",   Icon: User },
              { key: "break",    label: "Break",      Icon: Coffee },
              { key: "lunch",    label: "Lunch",      Icon: UtensilsCrossed },
              { key: "coaching", label: "Coaching",   Icon: Users2 },
              { key: "training", label: "Training",   Icon: GraduationCap },
              { key: "meeting",  label: "Meeting",    Icon: Users2 },
              { key: "eod",      label: "Done for day", Icon: LogOut },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                disabled={breakBusy}
                data-testid={`break-reason-${key}`}
                onClick={async () => {
                  setBreakBusy(true);
                  try {
                    // 1) STOP every in-flight call before opening the break.
                    //    Previously we only cancelled Power Dial sessions,
                    //    so a rep on a single-line manual call could hit
                    //    Pause → Bathroom and the call kept going — the
                    //    exact bug the user reported (2026-08-18).
                    if (powerSession) {
                      try { await cancelPowerDial(); } catch (_) { /* ignore */ }
                    }
                    // Kill any browser-held single-line call too.
                    try { callRef.current?.disconnect(); } catch (_) { /* ignore */ }
                    try { deviceRef.current?.disconnectAll(); } catch (_) { /* ignore */ }
                    callRef.current = null;
                    setCurrent(null);
                    setCallSid(null);
                    setCallStartedAt(null);
                    setCallState("idle");
                    setShowDispo(false);
                    setMuted(false);
                    // 2) Open a break record
                    const { data } = await api.post("/dialer/break/start", { reason: key });
                    setActiveBreak(data);
                    setShowBreakModal(false);
                    toast.success(`On break: ${label}`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Couldn't start break");
                  } finally { setBreakBusy(false); }
                }}
                className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-[var(--app-line)] bg-white hover:border-slate-400 hover:bg-slate-50 transition text-xs font-medium disabled:opacity-50"
              >
                <Icon size={20} className="text-slate-700" />
                {label}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ============================================================ AUTO-OFFER POP */}
      {/* Opens the second the customer picks up. Same shared state as the
          sidebar so tier picks / condition chips stay in sync between
          both surfaces. Rep can toggle it off via "Don't auto-open" —
          preference is persisted. */}
      <Dialog open={showOfferPopup} onOpenChange={setShowOfferPopup}>
        <DialogContent
          className="max-w-5xl p-0 gap-0"
          data-testid="dialer-auto-offer-modal"
        >
          {/* Header */}
          <div className="px-5 py-3 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <PhoneCall size={16} className="mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest opacity-90">Live call · in-call cockpit</div>
                <div className="text-sm font-semibold leading-tight truncate">
                  {current?.name || "Unknown seller"}
                </div>
                <div className="text-[11px] opacity-90 leading-tight mt-0.5 truncate">
                  <MapPin size={10} className="inline mr-1 -mt-0.5" />
                  {propInfo?.address || current?.property_address || "No address on file"}
                  {(propInfo?.city || current?.city) && (
                    <span className="opacity-80"> · {propInfo?.city || current?.city}{(propInfo?.state || current?.state) ? `, ${propInfo?.state || current?.state}` : ""}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {callState === "connected" && (
                <button
                  type="button"
                  onClick={transferToAcquisitions}
                  disabled={transferring || transferred}
                  data-testid="dialer-cockpit-transfer-btn"
                  title={transferNumber
                    ? `Warm-transfer this call to ${transferNumber}`
                    : "Admin must set an Acquisitions number in Settings first"}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold border-2 transition ${
                    transferred
                      ? "bg-emerald-700 border-emerald-100 text-white"
                      : "bg-emerald-500 border-emerald-100 text-white hover:bg-emerald-600 shadow-lg"
                  } ${(transferring || transferred) ? "opacity-90 cursor-not-allowed" : ""}`}
                >
                  {transferring ? (
                    <><Loader2 size={13} className="animate-spin" /> Transferring…</>
                  ) : transferred ? (
                    <><ArrowRightCircle size={13} /> Transferred</>
                  ) : (
                    <><ArrowRightCircle size={13} /> Transfer to Acquisitions</>
                  )}
                </button>
              )}
              {callState === "connected" && (
                <button
                  type="button"
                  onClick={toggleMute}
                  data-testid="dialer-cockpit-mute-btn"
                  title={muted ? "You are muted — seller can't hear you. Click to unmute." : "Mute yourself — seller won't hear you. Click again to unmute."}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold border-2 transition ${
                    muted
                      ? "bg-red-600 border-red-100 text-white shadow-lg animate-pulse"
                      : "bg-white/10 border-white/40 text-white hover:bg-white/20"
                  }`}
                >
                  {muted ? <MicOff size={14} /> : <Mic size={14} />}
                  {muted ? "MUTED" : "Mute"}
                </button>
              )}
              {(callState === "ringing" || callState === "connecting" || callState === "connected") && (
                <button
                  type="button"
                  onClick={hangUp}
                  data-testid="dialer-cockpit-end-call-btn"
                  title="End call — hangs up immediately and opens the disposition modal"
                  className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold border-2 bg-red-600 border-red-100 text-white shadow-lg hover:bg-red-700 transition"
                >
                  <PhoneOff size={14} />
                  End call
                </button>
              )}
              <label
                className="flex items-center gap-1 text-[10px] opacity-90 cursor-pointer select-none shrink-0"
                title="Auto-open on single-line dials. Speed Dialer always shows this — reps need context fast when parallel dialing."
              >
                <input
                  type="checkbox"
                  checked={autoOfferPopup}
                  onChange={(e) => {
                    setAutoOfferPopup(e.target.checked);
                    try { localStorage.setItem("dialer.autoOfferPopup", e.target.checked ? "1" : "0"); } catch { /* ignore */ }
                  }}
                  data-testid="dialer-auto-offer-toggle"
                  className="accent-white"
                />
                Auto-open on 1-line dial
              </label>
            </div>
          </div>

          {/* ============================= ENZO-STYLE 3-TAB IN-CALL COCKPIT =============================
              Lead Card  → property + owner facts, missing ARV banner, past offers.
              Script     → talk track with placeholder rendering + script picker.
              Deal Form  → owner price + condition chips + tier chips + offer + email.
              Send button pinned to footer so reps can ship from any tab. */}
          <Tabs defaultValue="lead" className="w-full" data-testid="dialer-in-call-tabs">
            <TabsList className="grid grid-cols-3 rounded-none border-b border-slate-200 bg-slate-50 h-11 p-0">
              <TabsTrigger
                value="lead"
                data-testid="dialer-in-call-tab-lead"
                className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 text-xs font-medium h-full"
              >
                <User size={13} className="mr-1.5" /> Lead Card
              </TabsTrigger>
              <TabsTrigger
                value="script"
                data-testid="dialer-in-call-tab-script"
                className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 text-xs font-medium h-full"
              >
                <BookText size={13} className="mr-1.5" /> Script
                {scripts.length > 0 && (
                  <Badge variant="secondary" className="ml-1.5 h-4 px-1.5 text-[9px]">{scripts.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="deal"
                data-testid="dialer-in-call-tab-deal"
                className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 text-xs font-medium h-full"
              >
                <FileSignature size={13} className="mr-1.5" /> Deal Form
              </TabsTrigger>
            </TabsList>

            {/* ------ LEAD CARD TAB ------ */}
            <TabsContent value="lead" className="p-5 max-h-[60vh] overflow-y-auto space-y-4 mt-0" data-testid="dialer-in-call-lead-panel">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="dialer-modal-facts">
                <FactCell label="ARV" value={propInfo?.arv ? `$${Math.round(propInfo.arv).toLocaleString()}` : "—"} />
                <FactCell label="Home value" value={propInfo?.property_value ? `$${Math.round(propInfo.property_value).toLocaleString()}` : "—"} />
                <FactCell label="Equity" value={propInfo?.equity_pct != null ? `${Math.round(propInfo.equity_pct)}%` : "—"} />
                <FactCell label="Mortgage" value={propInfo?.mortgage_balance ? `$${Math.round(propInfo.mortgage_balance).toLocaleString()}` : "—"} />
                <FactCell label="Sqft" value={propInfo?.sqft ? Number(propInfo.sqft).toLocaleString() : "—"} />
                <FactCell label="Motivation" value={propInfo?.motivation_score != null ? `${Math.round(propInfo.motivation_score)}/100` : "—"} tone="orange" />
                <FactCell label="Owner" value={propInfo?.owner || current?.name || "—"} />
                <FactCell label="Phone" value={current?.phone || "—"} />
              </div>

              {(!propInfo?.arv || Number(propInfo?.arv) <= 0) && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2"
                     data-testid="dialer-modal-missing-arv-banner">
                  <div className="flex items-start gap-2">
                    {autoLookupBusy ? (
                      <Loader2 size={14} className="text-amber-700 mt-0.5 shrink-0 animate-spin" />
                    ) : (
                      <Sparkles size={14} className="text-amber-700 mt-0.5 shrink-0" />
                    )}
                    <div className="text-xs text-amber-900 leading-snug flex-1">
                      <strong>{autoLookupBusy ? "Pulling property data…" : "Missing property data."}</strong>{" "}
                      {autoLookupBusy
                        ? "This runs in the background — keep the seller on the line, tiers will fill themselves."
                        : "Type an ARV in the Deal Form or run a fresh Property Lookup — tiers will update automatically."}
                    </div>
                    {!autoLookupBusy && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={runPropertyLookupNow}
                        data-testid="dialer-modal-lookup-btn"
                        className="h-7 rounded-full text-[11px] border-amber-400 text-amber-900 hover:bg-amber-100"
                      >
                        Run Lookup
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {pastOffers.length > 0 && (
                <div className="rounded-xl border border-slate-200 p-3 bg-slate-50"
                     data-testid="dialer-modal-past-offers">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">
                    Past offers ({pastOffers.length})
                  </div>
                  <div className="space-y-1">
                    {pastOffers.slice(0, 3).map((o) => (
                      <div key={o.id} className="flex items-center justify-between text-xs">
                        <div className="font-mono">${Math.round(Number(o.purchase_price) || 0).toLocaleString()}</div>
                        <div className="text-[10px] text-slate-500 capitalize">{o.status || "draft"}</div>
                        <div className="text-[10px] text-slate-400">{(o.created_at || "").slice(0, 10)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ------ SCRIPT TAB ------ */}
            <TabsContent value="script" className="p-5 max-h-[60vh] overflow-y-auto space-y-3 mt-0" data-testid="dialer-in-call-script-panel">
              {scripts.length > 0 ? (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                      <BookText size={10} /> Talk track
                    </div>
                    {scripts.length > 1 && (
                      <select
                        value={activeScript?.id || ""}
                        onChange={(e) => setActiveScript(scripts.find((s) => s.id === e.target.value) || null)}
                        className="text-[11px] rounded-md border border-[var(--app-line)] px-2 py-1 bg-white"
                        data-testid="dialer-in-call-script-select"
                      >
                        {scripts.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}{s.is_default ? " ★" : ""}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  {activeScript ? (
                    <div
                      className="text-sm whitespace-pre-wrap leading-relaxed rounded-lg border border-slate-200 bg-white p-4 font-[system-ui] max-h-[45vh] overflow-y-auto"
                      data-testid="dialer-in-call-script-body"
                    >
                      {renderedScript(activeScript.body)}
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground italic">No script selected</div>
                  )}
                  <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Sparkles size={9} />
                    Placeholders auto-filled: {"{name} {address} {phone} {city} {state}"}
                  </div>
                </>
              ) : (
                <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center" data-testid="dialer-in-call-no-script">
                  <BookText size={20} className="mx-auto text-slate-400 mb-2" />
                  <div className="text-sm font-medium text-slate-700">No scripts yet</div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Add a talk track from the top-right &quot;Scripts&quot; button so reps can read while they dial.
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ------ DEAL FORM TAB ------ */}
            <TabsContent value="deal" className="p-5 max-h-[60vh] overflow-y-auto space-y-4 mt-0" data-testid="dialer-in-call-deal-panel">
              {/* Manual ARV / Sqft when property lookup missed */}
              {(!propInfo?.arv || Number(propInfo?.arv) <= 0) && (
                <div className="grid grid-cols-2 gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <div>
                    <Label className="text-[10px] text-amber-900">ARV (est. after repair)</Label>
                    <Input
                      type="number"
                      defaultValue={propInfo?.arv || ""}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (v > 0) setPropInfo((p) => ({ ...(p || {}), arv: v }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const v = Number(e.currentTarget.value);
                          if (v > 0) setPropInfo((p) => ({ ...(p || {}), arv: v }));
                        }
                      }}
                      data-testid="dialer-modal-manual-arv-input"
                      placeholder="e.g. 300000"
                      className="mt-1 h-9 text-sm font-mono bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-amber-900">Sqft (optional)</Label>
                    <Input
                      type="number"
                      defaultValue={propInfo?.sqft || ""}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (v > 0) setPropInfo((p) => ({ ...(p || {}), sqft: v }));
                      }}
                      data-testid="dialer-modal-manual-sqft-input"
                      placeholder="e.g. 1600"
                      className="mt-1 h-9 text-sm font-mono bg-white"
                    />
                  </div>
                </div>
              )}

              {/* Owner Price */}
              <div>
                <Label className="text-[10px] flex items-center gap-1 text-emerald-800">
                  <DollarSign size={10} /> Owner / Asking Price
                  <span className="text-[9px] font-normal opacity-70">(overrides ARV as tier base)</span>
                </Label>
                <Input
                  type="number"
                  value={ownerPrice}
                  onChange={(e) => setOwnerPrice(e.target.value)}
                  className="mt-1 h-9 text-sm font-mono"
                  data-testid="dialer-modal-owner-price-input"
                  placeholder={propInfo?.arv ? `e.g. $${Math.round(Number(propInfo.arv) * 0.9).toLocaleString()}` : "e.g. 220000"}
                />
              </div>

              {/* Condition chips */}
              <div>
                <Label className="text-[10px] flex items-center gap-1 text-emerald-800">
                  <Wrench size={10} /> Repair condition
                  <span className="text-[9px] font-normal opacity-70">(from what the seller just said)</span>
                </Label>
                <div className="grid grid-cols-4 gap-1.5 mt-1" data-testid="dialer-modal-condition-chips">
                  {[
                    { key: "cosmetic", label: "Cosmetic", rate: "$12" },
                    { key: "medium",   label: "Medium",   rate: "$28" },
                    { key: "heavy",    label: "Heavy",    rate: "$55" },
                    { key: "teardown", label: "Teardown", rate: "$100" },
                  ].map((c) => {
                    const isActive = conditionKey === c.key;
                    const tones = {
                      cosmetic: isActive ? "bg-emerald-600 text-white border-transparent" : "bg-white text-emerald-900 border-emerald-300 hover:bg-emerald-50",
                      medium:   isActive ? "bg-sky-600 text-white border-transparent"     : "bg-white text-sky-900 border-sky-300 hover:bg-sky-50",
                      heavy:    isActive ? "bg-orange-600 text-white border-transparent"  : "bg-white text-orange-900 border-orange-300 hover:bg-orange-50",
                      teardown: isActive ? "bg-rose-700 text-white border-transparent"    : "bg-white text-rose-900 border-rose-300 hover:bg-rose-50",
                    };
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setConditionKey(c.key)}
                        data-testid={`dialer-modal-condition-${c.key}`}
                        className={`rounded-lg border py-2 text-center transition-colors ${tones[c.key]}`}
                      >
                        <div className="text-[10px] font-semibold uppercase tracking-wider">{c.label}</div>
                        <div className={`text-[9px] mt-0.5 ${isActive ? "opacity-90" : "opacity-60"}`}>{c.rate}/sqft</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tier chips */}
              {tiers?.tiers?.length > 0 && (
                <div>
                  <Label className="text-[10px] flex items-center gap-1 text-emerald-800">
                    <Sparkles size={10} /> Offer tiers
                    {tiersLoading && <Loader2 size={10} className="animate-spin ml-1 opacity-70" />}
                  </Label>
                  <div className="grid grid-cols-3 gap-2 mt-1"
                       data-testid="dialer-modal-smart-tiers">
                    {tiers.tiers.map((t) => {
                      const isActive = selectedTier === t.key;
                      const palette = t.key === "quick"
                        ? { chip: "bg-emerald-600 text-white", idle: "bg-white text-emerald-900 border-emerald-300 hover:bg-emerald-50" }
                        : t.key === "max_safe"
                        ? { chip: "bg-amber-600 text-white",   idle: "bg-white text-amber-900 border-amber-300 hover:bg-amber-50" }
                        : { chip: "bg-violet-600 text-white",  idle: "bg-white text-violet-900 border-violet-300 hover:bg-violet-50" };
                      return (
                        <button
                          key={`modal-${t.key}-${Math.round(t.price)}`}
                          data-testid={`dialer-modal-tier-${t.key}`}
                          onClick={() => {
                            setSelectedTier(t.key);
                            setOfferPrice(String(Math.round(t.price)));
                          }}
                          className={`rounded-xl border p-3 text-left transition-colors ${isActive ? palette.chip + " border-transparent" : palette.idle}`}
                        >
                          <div className="text-[9px] uppercase tracking-widest opacity-80">
                            {t.key === "quick" ? "Quick" : t.key === "max_safe" ? "Max Safe" : "Recommended"}
                          </div>
                          <div className="font-mono font-bold text-lg mt-0.5 leading-tight">
                            ${Math.round(t.price).toLocaleString()}
                          </div>
                          <div className={`text-[9px] mt-0.5 ${isActive ? "opacity-90" : "opacity-60"}`}>
                            {t.arv_pct}% {tiers?.base_source === "owner_price" ? "Owner" : "ARV"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Offer + Email */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200">
                <div>
                  <Label className="text-[10px]">Offer amount</Label>
                  <Input
                    type="number"
                    value={offerPrice}
                    onChange={(e) => setOfferPrice(e.target.value)}
                    className="mt-1 h-9 text-sm font-mono"
                    data-testid="dialer-modal-offer-price-input"
                    placeholder="250000"
                  />
                </div>
                <div>
                  <Label className="text-[10px]">Seller email (for signature)</Label>
                  {knownEmails.length > 0 && emailMode === "select" ? (
                    <select
                      value={sellerEmail}
                      onChange={(e) => {
                        if (e.target.value === "__new__") {
                          setEmailMode("new");
                          setSellerEmail("");
                        } else setSellerEmail(e.target.value);
                      }}
                      className="mt-1 h-9 w-full rounded-md border border-input bg-white px-2 text-xs"
                      data-testid="dialer-modal-seller-email-select"
                    >
                      {knownEmails.map((em) => (<option key={em} value={em}>{em}</option>))}
                      <option value="__new__">+ Add new email…</option>
                    </select>
                  ) : (
                    <Input
                      type="email"
                      value={sellerEmail}
                      onChange={(e) => setSellerEmail(e.target.value)}
                      className="mt-1 h-9 text-xs"
                      data-testid="dialer-modal-seller-email-input"
                      placeholder="seller@example.com"
                    />
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Persistent footer — Send offer is one click away from any tab */}
          <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex gap-2 items-center">
            <Button
              variant="outline"
              onClick={() => setShowOfferPopup(false)}
              data-testid="dialer-modal-minimize-btn"
              className="rounded-full h-10"
            >
              Minimize
            </Button>
            <div className="flex-1 text-[10px] text-slate-500 text-center leading-tight">
              PDF generated + emailed via SignWell mid-call.
            </div>
            <Button
              onClick={sendOfferNow}
              disabled={sendingOffer || !offerPrice || !sellerEmail}
              data-testid="dialer-modal-send-offer-btn"
              className="rounded-full h-10 font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-6"
            >
              {sendingOffer ? (
                <><Loader2 size={14} className="animate-spin mr-1.5" /> Sending…</>
              ) : (
                <><FileSignature size={14} className="mr-1.5" /> Send offer</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StateBadge({ state, startedAt }) {
  const [elapsed, setElapsed] = useState("00:00");
  useEffect(() => {
    if (state !== "connected" || !startedAt) return;
    const id = setInterval(() => {
      const s = Math.floor((Date.now() - startedAt) / 1000);
      const mm = String(Math.floor(s / 60)).padStart(2, "0");
      const ss = String(s % 60).padStart(2, "0");
      setElapsed(`${mm}:${ss}`);
    }, 500);
    return () => clearInterval(id);
  }, [state, startedAt]);

  const map = {
    idle: { txt: "Idle", cls: "bg-slate-100 text-slate-700" },
    connecting: { txt: "Connecting…", cls: "bg-amber-100 text-amber-800 animate-pulse" },
    ringing: { txt: "Ringing…", cls: "bg-blue-100 text-blue-700 animate-pulse" },
    connected: { txt: `● Live · ${elapsed}`, cls: "bg-emerald-100 text-emerald-800" },
    ended: { txt: "Call ended", cls: "bg-gray-100 text-gray-700" },
  };
  const s = map[state] || map.idle;
  return (
    <div className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${s.cls}`}
         data-testid="dialer-state-badge">
      {s.txt}
    </div>
  );
}

// Compact fact card used inside the in-call offer modal — always renders a
// value (falls back to "—") so the panel is never empty even when property
// data hasn't loaded yet.
function FactCell({ label, value, tone = "slate" }) {
  const toneCls = {
    slate:   "text-slate-800",
    orange:  "text-orange-700",
    emerald: "text-emerald-700",
  }[tone] || "text-slate-800";
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 truncate">{label}</div>
      <div className={`font-mono font-semibold text-sm truncate ${toneCls}`}>{value || "—"}</div>
    </div>
  );
}



function PropRow({ label, value, icon: Icon }) {
  const empty = value == null || value === "" || value === false;
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <div className="text-muted-foreground flex items-center gap-1.5">
        {Icon && <Icon size={10} />}
        {label}
      </div>
      <div className={`font-medium ${empty ? "text-muted-foreground/50 italic" : ""}`}>
        {empty ? "—" : value}
      </div>
    </div>
  );
}


/* ============================================================================
 * ManageDialog — CRUD for scripts and voicemail drops
 * ==========================================================================*/
function ManageDialog({ kind, onClose, scripts, voicemails, onScriptsChanged, onVoicemailsChanged }) {
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [voice, setVoice] = useState("Polly.Joanna-Neural");
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const nameInputRef = useRef(null);

  const open  = kind === "scripts" || kind === "vms";
  const isVm  = kind === "vms";
  const list  = isVm ? voicemails : scripts;
  const label = isVm ? "voicemail" : "script";

  useEffect(() => {
    if (open) startNew();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind]);

  const startNew = () => {
    setEditing(null); setName(""); setBody("");
    setVoice("Polly.Joanna-Neural"); setIsDefault(false);
    // Jump the cursor into the Name field so the rep sees the state change
    // and can immediately start typing — otherwise the button "looks dead"
    // because the form was already empty.
    setTimeout(() => nameInputRef.current?.focus(), 60);
  };
  const loadIntoForm = (item) => {
    setEditing(item.id);
    setName(item.name || ""); setBody(item.body || "");
    setVoice(item.voice || "Polly.Joanna-Neural");
    setIsDefault(!!item.is_default);
  };
  const save = async () => {
    if (!name.trim() || !body.trim()) {
      return toast.error("Name and body are required");
    }
    setSaving(true);
    try {
      const payload = isVm
        ? { name, body, voice, is_default: isDefault }
        : { name, body, is_default: isDefault };
      const base = isVm ? "/dialer/voicemails" : "/dialer/scripts";
      if (editing) {
        await api.patch(`${base}/${editing}`, payload);
        toast.success(`Updated ${label}`);
      } else {
        await api.post(base, payload);
        toast.success(`Saved ${label}`);
      }
      (isVm ? onVoicemailsChanged : onScriptsChanged)?.();
      startNew();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const del = async (id) => {
    if (!window.confirm(`Delete this ${label}?`)) return;
    try {
      await api.delete(`${isVm ? "/dialer/voicemails" : "/dialer/scripts"}/${id}`);
      (isVm ? onVoicemailsChanged : onScriptsChanged)?.();
      if (editing === id) startNew();
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  // ---- Voicemail preview — hits our backend which uses OpenAI TTS to
  // generate a natural-sounding MP3. This is dramatically closer to what
  // Twilio's Polly-Neural voice plays on a real call than the browser's
  // built-in speechSynthesis (which sounds robotic).
  const audioRef = useRef(null);
  const speakPreview = async () => {
    if (!isVm || !body.trim()) return;
    // Cancel any in-flight preview
    try { audioRef.current?.pause(); } catch (_) { /* ignore */ }
    audioRef.current = null;
    setPreviewing(true);
    try {
      const resp = await api.post(
        "/dialer/voicemails/preview-audio",
        { body, voice },
        { responseType: "blob", timeout: 25000 },
      );
      const url = URL.createObjectURL(resp.data);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => { setPreviewing(false); URL.revokeObjectURL(url); };
      a.onerror = () => { setPreviewing(false); URL.revokeObjectURL(url); toast.error("Playback failed"); };
      await a.play();
    } catch (e) {
      setPreviewing(false);
      const detail = e?.response?.data?.detail || e?.message || "Preview failed";
      toast.error(String(detail));
    }
  };
  const stopPreview = () => {
    try { audioRef.current?.pause(); } catch (_) { /* ignore */ }
    audioRef.current = null;
    setPreviewing(false);
  };


  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid={`dialer-manage-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle>
            {isVm ? "Manage voicemail drops" : "Manage call scripts"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-[220px_1fr] gap-4 pt-2">
          {/* List */}
          <div className="space-y-1.5">
            <Button size="sm" variant="outline" onClick={startNew}
                    data-testid="dialer-manage-new-btn"
                    className="w-full rounded-full text-xs h-8">
              <Plus size={12} className="mr-1" /> New {label}
            </Button>
            <div className="max-h-[380px] overflow-y-auto space-y-1 mt-1.5">
              {list.map((it) => (
                <div key={it.id}
                     className={`p-2 rounded-lg border text-xs cursor-pointer flex items-center justify-between gap-1 ${
                       editing === it.id
                         ? "border-[var(--app-accent)] bg-[var(--app-accent)]/5"
                         : "border-[var(--app-line)] hover:border-[var(--app-accent)]/50"
                     }`}>
                  <button onClick={() => loadIntoForm(it)} className="text-left flex-1 truncate"
                          data-testid={`dialer-manage-item-${it.id}`}>
                    {it.name} {it.is_default && "★"}
                  </button>
                  <button onClick={() => del(it.id)}
                          data-testid={`dialer-manage-del-${it.id}`}
                          className="text-red-600 hover:text-red-800 p-0.5">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              {list.length === 0 && (
                <div className="text-[11px] text-muted-foreground italic p-2 text-center">
                  No {label}s yet
                </div>
              )}
            </div>
          </div>

          {/* Editor */}
          <div className="space-y-3">
            <div className="rounded-lg border px-3 py-2 flex items-center gap-2 text-[11px]"
                 data-testid="dialer-manage-editor-banner"
                 style={{
                   borderColor: editing ? "var(--app-accent)" : "#10b98166",
                   background: editing ? "var(--app-accent)0d" : "#10b9810f",
                   color: editing ? "var(--app-accent-dark)" : "#047857",
                 }}>
              {editing ? (
                <>
                  <Pencil size={11} />
                  <span>Editing <b className="font-semibold">{name || "…"}</b></span>
                </>
              ) : (
                <>
                  <Plus size={11} />
                  <span>Creating a new {label} — fill in the fields below and hit Create.</span>
                </>
              )}
            </div>
            <div>
              <Label className="text-[11px]">Name</Label>
              <Input ref={nameInputRef}
                     value={name} onChange={(e) => setName(e.target.value)}
                     className="mt-1 h-9 text-sm"
                     placeholder={isVm ? "Standard drop" : "Motivated seller"}
                     data-testid="dialer-manage-name-input" />
            </div>
            <div>
              <Label className="text-[11px]">
                {isVm ? "Message (text-to-speech)" : "Talk track"}
              </Label>
              <Textarea rows={isVm ? 4 : 8}
                        value={body} onChange={(e) => setBody(e.target.value)}
                        className="mt-1 text-xs font-mono"
                        placeholder={isVm
                          ? "Hi {first_name}, this is {rep_first_name}. I was calling about your property located at {property_address}..."
                          : "Hi, is this {name}? I saw your property at {address}..."}
                        data-testid="dialer-manage-body-input" />
              {!isVm && (
                <div className="text-[10px] text-muted-foreground mt-1">
                  Placeholders: {"{name} {address} {phone} {city} {state}"}
                </div>
              )}
              {isVm && (
                <div className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
                  Placeholders auto-fill on drop:{" "}
                  <code className="bg-[var(--app-surface)] px-1 rounded">{"{first_name}"}</code>{" "}
                  <code className="bg-[var(--app-surface)] px-1 rounded">{"{property_address}"}</code>{" "}
                  <code className="bg-[var(--app-surface)] px-1 rounded">{"{rep_first_name}"}</code>{" "}
                  <code className="bg-[var(--app-surface)] px-1 rounded">{"{rep_name}"}</code>{" "}
                  <code className="bg-[var(--app-surface)] px-1 rounded">{"{city}"}</code>{" "}
                  <code className="bg-[var(--app-surface)] px-1 rounded">{"{state}"}</code>
                </div>
              )}
            </div>
            {isVm && (
              <div>
                <Label className="text-[11px]">Voice (used on real calls via Twilio Polly)</Label>
                <select value={voice} onChange={(e) => setVoice(e.target.value)}
                        className="mt-1 h-9 w-full rounded-md border border-input px-2 text-sm bg-white"
                        data-testid="dialer-manage-voice-select">
                  <optgroup label="US Female — Neural (recommended)">
                    <option value="Polly.Joanna-Neural">Joanna — warm, professional</option>
                    <option value="Polly.Kimberly-Neural">Kimberly — mature, calm</option>
                    <option value="Polly.Ivy-Neural">Ivy — young, energetic</option>
                    <option value="Polly.Salli-Neural">Salli — friendly</option>
                    <option value="Polly.Ruth-Neural">Ruth — soft, personable</option>
                  </optgroup>
                  <optgroup label="US Male — Neural (recommended)">
                    <option value="Polly.Matthew-Neural">Matthew — deep, authoritative</option>
                    <option value="Polly.Justin-Neural">Justin — young, casual</option>
                    <option value="Polly.Stephen-Neural">Stephen — clear, confident</option>
                    <option value="Polly.Kevin-Neural">Kevin — expressive, storyteller</option>
                  </optgroup>
                  <optgroup label="Standard (cheaper, more robotic)">
                    <option value="Polly.Joanna">Joanna (Standard)</option>
                    <option value="Polly.Matthew">Matthew (Standard)</option>
                  </optgroup>
                </select>
                <div className="text-[10px] text-muted-foreground mt-1">
                  Neural voices sound almost human on real calls. Preview button below uses OpenAI TTS to approximate.
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={isDefault}
                     onChange={(e) => setIsDefault(e.target.checked)}
                     data-testid="dialer-manage-default-check"
                     className="accent-[var(--app-accent)]" />
              Set as default
            </label>
          </div>
        </div>
        <DialogFooter>
          {isVm && (
            <Button
              type="button"
              variant="outline"
              onClick={previewing ? stopPreview : speakPreview}
              disabled={!body.trim()}
              data-testid="dialer-manage-preview-btn"
              className="rounded-full h-9 mr-auto border-purple-300 text-purple-700 hover:bg-purple-50"
              title="Speak the voicemail out loud using a demo lead + your name"
            >
              {previewing ? (
                <><X size={12} className="mr-1.5" /> Stop preview</>
              ) : (
                <><Play size={12} className="mr-1.5" /> Preview voice</>
              )}
            </Button>
          )}
          <Button onClick={save} disabled={saving} data-testid="dialer-manage-save-btn"
                  className="rounded-full h-9">
            {saving ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Save size={12} className="mr-1.5" />}
            {editing ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Enzo-parity Today-bar stat cell — small, dense, one-glance count.
function StatCell({ label, value, tone = "slate", bold = false }) {
  const toneMap = {
    slate:   "text-slate-900",
    sky:     "text-sky-700",
    emerald: "text-emerald-700",
    violet:  "text-violet-700",
    amber:   "text-amber-700",
  };
  return (
    <div className="flex flex-col items-start leading-tight">
      <span className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className={`text-lg tabular-nums ${bold ? "font-bold" : "font-semibold"} ${toneMap[tone] || toneMap.slate}`}>
        {value}
      </span>
    </div>
  );
}


// Inline auto-dispose countdown pill. Renders inside the disposition
// modal title. When `seconds` > 0 and the modal is open, counts down
// and fires `onFire()` once. Rep can silence it by clicking any dispo
// chip (which cancels the effect via unmount).
function AutoDisposeCountdown({ show, seconds, onFire }) {
  const [left, setLeft] = useState(seconds || 0);
  const firedRef = useRef(false);
  useEffect(() => {
    firedRef.current = false;
    setLeft(seconds || 0);
    if (!show || !seconds) return;
    const started = Date.now();
    const id = setInterval(() => {
      const gone = Math.floor((Date.now() - started) / 1000);
      const remaining = Math.max(0, seconds - gone);
      setLeft(remaining);
      if (remaining <= 0 && !firedRef.current) {
        firedRef.current = true;
        clearInterval(id);
        try { onFire && onFire(); } catch (_) { /* ignore */ }
      }
    }, 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, seconds]);
  if (!show || !seconds || left <= 0) return null;
  return (
    <span
      className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-800"
      data-testid="dialer-auto-dispose-countdown"
      title="No answer will auto-fire so the next call starts on time"
    >
      <Clock size={10} /> auto {left}s
    </span>
  );
}
